<template>
  <div class="review-tab">
    <!-- Review-only bits join DeliverableStatusBar in the modal's tab row -->
    <Teleport defer to="#deliverable-toolbar-extras" :disabled="!hasToolbarSlot">
    <div v-if="isActive && hasFinalizationItems" class="review-tab-actions">
      <div v-if="hasFinalizationItems" class="detail-item">
        <label class="label is-small">Finalization</label>
        <span class="tag" :class="finalizationMeta.cardClass">
          <span class="icon is-small mr-1">
            <font-awesome-icon :icon="['fal', finalizationMeta.icon]" />
          </span>
          {{ finalizationMeta.label }}
        </span>
      </div>
    </div>
    </Teleport>

    <!-- Loading state -->
    <div v-if="store.loading && !store.initialized" class="empty-state">
      <div class="has-text-centered py-6">
        <BrandLoader :size="'3x'" />
        <p class="has-text-grey mt-3">Loading review...</p>
      </div>
    </div>

    <!-- Listener gave up (offline / rules denial): say so instead of silently
         showing the drop zone, which invites a duplicate re-upload -->
    <div v-else-if="store.error && !store.initialized" class="empty-state">
      <div class="has-text-centered py-6">
        <span class="icon is-large has-text-danger">
          <font-awesome-icon :icon="['fal', 'triangle-exclamation']" size="2x" />
        </span>
        <p class="has-text-grey mt-3">Couldn't load this review.</p>
        <button class="button is-small mt-3" @click="retryLoad">
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'rotate-right']" />
          </span>
          <span>Retry</span>
        </button>
      </div>
    </div>

    <!-- Empty state / Add files: drop zone -->
    <div v-else-if="!isEpisodeReview && store.uploadQueue.length === 0 && (!store.review || store.sortedItems.length === 0 || stayOnQueueView)" class="empty-state">
      <div class="drop-zone-wrapper">
        <ClientFileDropZone
          accept="video/*,image/*,audio/*,.pdf"
          @files-selected="onFilesSelected"
        />
        <button
          v-if="store.sortedItems.length > 0"
          class="button is-small mt-3"
          @click="stayOnQueueView = false"
        >
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'arrow-left']" />
          </span>
          <span>Back to review</span>
        </button>
      </div>
    </div>

    <!-- Upload queue panel (shown when files are queued but no review items yet) -->
    <div v-else-if="!isEpisodeReview && store.uploadQueue.length > 0 && (store.sortedItems.length === 0 || stayOnQueueView)" class="empty-state">
      <div class="upload-queue-stage">
        <!-- Compact drop zone — same dashed border language, just shorter -->
        <ClientFileDropZone
          accept="video/*,image/*,audio/*,.pdf"
          @files-selected="onFilesSelected"
          class="compact-drop-zone"
        />

        <!-- File list header -->
        <div class="is-flex is-align-items-center is-justify-content-space-between mt-4 mb-2">
          <p class="is-size-7 has-text-weight-semibold has-text-grey">
            {{ queuedCount }} file{{ queuedCount !== 1 ? 's' : '' }} ready
          </p>
          <span class="queue-meta-tag">{{ queueTotalSize }}</span>
        </div>

        <!-- File list -->
        <div class="queue-file-list">
          <div
            v-for="qItem in store.uploadQueue"
            :key="qItem.id"
            class="queue-file-item"
            :class="{
              'is-uploading': qItem.status === 'uploading',
              'is-paused': qItem.status === 'paused',
              'is-done': qItem.status === 'done',
              'is-error': qItem.status === 'error'
            }"
          >
            <!-- Card-as-progress-bar: fill sweeps behind content -->
            <div
              v-if="qItem.status !== 'queued'"
              class="queue-progress-bg"
              :class="{
                'is-primary': qItem.status === 'uploading',
                'is-warning': qItem.status === 'paused',
                'is-success': qItem.status === 'done',
                'is-danger': qItem.status === 'error'
              }"
            >
              <div class="progress-fill" :style="{ width: qItem.status === 'done' ? '100%' : qItem.progress + '%' }"></div>
            </div>

            <div class="is-flex is-align-items-center" style="gap: 0.75rem">
              <!-- File type icon -->
              <div class="queue-item-thumb">
                <img
                  v-if="qItem.type === 'image' && qItem.status === 'queued'"
                  :src="getQueueItemPreview(qItem)"
                  class="queue-thumb-img"
                />
                <span v-else class="icon has-text-primary">
                  <font-awesome-icon :icon="queueItemIcon(qItem.type)" />
                </span>
              </div>

              <!-- Title + size -->
              <div class="is-flex-grow-1" style="min-width: 0">
                <input
                  v-if="qItem.status === 'queued'"
                  class="input is-small queue-title-input"
                  type="text"
                  placeholder="Enter title..."
                  :value="qItem.title"
                  @input="store.updateQueueItemTitle(qItem.id, $event.target.value)"
                />
                <p v-else class="is-size-7 has-text-weight-medium has-text-ellipsis">{{ qItem.title }}</p>
                <p class="is-size-7 has-text-grey mt-1">
                  {{ formatFileSize(qItem.file.size) }}
                  <span v-if="qItem.status === 'uploading' || qItem.status === 'paused'" class="has-text-primary has-text-weight-semibold ml-2">
                    <template v-if="qItem.status === 'uploading' && qItem.progress >= 100">Finalizing…</template>
                    <template v-else>{{ qItem.progress }}%<template v-if="qItem.status === 'paused'"> · paused</template></template>
                  </span>
                </p>
                <p v-if="qItem.status === 'error' && qItem.error" class="is-size-7 has-text-danger mt-1">
                  {{ qItem.error }}
                </p>
                <label v-if="qItem.status === 'queued'" class="checkbox is-size-7 mt-1">
                  <input
                    type="checkbox"
                    :checked="qItem.requiresFinalization || false"
                    @change="updateQueueItemFinalization(qItem.id, $event.target.checked)"
                  />
                  Requires finalization
                </label>
              </div>

              <!-- Status / remove -->
              <div class="queue-item-action">
                <button
                  v-if="qItem.status === 'queued'"
                  class="button is-small is-danger"
                  @click="store.removeQueueItem(qItem.id)"
                  title="Remove from queue"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'times']" />
                  </span>
                </button>
                <button
                  v-else-if="qItem.status === 'uploading'"
                  class="button is-small is-text"
                  @click="store.pauseQueueItem(qItem.id)"
                  title="Pause upload"
                >
                  <span class="icon is-small has-text-primary">
                    <font-awesome-icon :icon="['fas', 'pause']" size="sm" />
                  </span>
                </button>
                <button
                  v-else-if="qItem.status === 'paused'"
                  class="button is-small is-text"
                  @click="store.resumeQueueItem(qItem.id)"
                  title="Resume upload"
                >
                  <span class="icon is-small has-text-primary">
                    <font-awesome-icon :icon="['fas', 'play']" size="sm" />
                  </span>
                </button>
                <span v-else-if="qItem.status === 'done'" class="icon is-small has-text-success">
                  <font-awesome-icon :icon="['fas', 'check']" size="sm" />
                </span>
                <template v-else-if="qItem.status === 'error'">
                  <button
                    class="button is-small is-text"
                    :disabled="store.isUploading"
                    @click="retryQueueUpload(qItem.id)"
                    title="Retry upload"
                  >
                    <span class="icon is-small has-text-primary">
                      <font-awesome-icon :icon="['fas', 'rotate-right']" size="sm" />
                    </span>
                  </button>
                  <button
                    class="button is-small is-text"
                    @click="store.removeQueueItem(qItem.id)"
                    title="Remove from queue"
                  >
                    <span class="icon is-small has-text-danger">
                      <font-awesome-icon :icon="['fal', 'times']" size="sm" />
                    </span>
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Overall progress -->
        <p v-if="store.isUploading" class="has-text-centered is-size-7 has-text-grey mt-3">
          {{ uploadedCount }} of {{ store.uploadQueue.length }} uploaded
        </p>

        <!-- Start upload button -->
        <div class="has-text-centered mt-4">
          <button
            class="button is-primary"
            :class="{ 'is-loading': store.isUploading }"
            :disabled="store.isUploading || !hasQueuedItems"
            @click="startQueueUpload"
          >
            <span class="icon is-small">
              <font-awesome-icon :icon="['fas', 'cloud-upload']" />
            </span>
            <span>Start Upload</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Review with items -->
    <template v-else>
      <div
        v-if="frozenClientReviewItems.length > 0"
        class="notification is-warning is-light frozen-client-banner"
      >
        <div class="is-flex is-align-items-center is-justify-content-space-between">
          <div>
            <p class="has-text-weight-semibold mb-1">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mr-2" />
              Some items aren't visible to the client yet
            </p>
            <p class="is-size-7">
              This deliverable is in client review, but
              {{ frozenClientReviewItems.length }}
              {{ frozenClientReviewItems.length === 1 ? 'item has' : 'items have' }}
              a newer version that hasn't been promoted to the client.
            </p>
          </div>
          <button
            class="button is-warning is-small"
            :disabled="promotingFrozenItems"
            @click="onPromoteFrozenItems"
          >
            {{ promotingFrozenItems ? 'Promoting…' : 'Promote current versions' }}
          </button>
        </div>
      </div>
      <div class="columns is-gapless review-columns">
      <!-- Left: Thumbnail sidebar -->
      <div class="column thumbnail-sidebar" :class="[isEpisodeReview ? 'is-one-quarter' : 'is-narrow-rail', { 'is-collapsed': railCollapsed.assets }]">
        <button class="rail-expander" title="Show assets" aria-label="Show assets" @click="toggleRail('assets')">
          <font-awesome-icon :icon="['fal', 'chevrons-right']" />
          <span class="rail-expander-label">{{ isEpisodeReview ? 'Episode Assets' : 'Assets' }}</span>
        </button>
        <div class="is-flex is-align-items-center px-3 pt-3 pb-2">
          <p class="title is-6 mb-0">{{ isEpisodeReview ? 'Episode Assets' : 'Assets' }}</p>
        </div>
        <DeliverableStatusBar
          v-if="reviewItems.length > 0"
          part="review"
          class="px-3 pb-2"
          :deliverable="props.deliverable"
          :project="liveProject"
          :company="props.company"
        />
        <EpisodeReviewSlots
          v-if="isEpisodeReview"
          :deliverable="props.deliverable"
          :project="episodeProject"
          :items="reviewItems"
          :activeIndex="activeItemIndex"
          :companyId="props.company?.id || liveProject?.company || null"
          :metaActive="episodeMetaView"
          :finalAssets="props.finalAssets"
          @select="onEpisodeSelectMedia"
          @select-meta="onEpisodeSelectMeta"
          @remove="removeItem"
          @replace="startReplace"
          @rename="startItemRename"
          @download="downloadItem"
        />

        <ReviewThumbnailStrip
          v-else
          :items="reviewItems"
          :activeIndex="activeItemIndex"
          layout="vertical"
          reviewTags
          @select="selectItem"
        >
          <template #card-menu="{ item, index }">
            <button class="thumb-menu-item" :disabled="isVersionBusy" @click="startReplace(index)">
              <span>Replace v{{ item.versionCount || 1 }} file…</span>
              <small>Fixes a bad export. Keeps comments.</small>
            </button>
            <button class="thumb-menu-item" @click="startItemRename(index)">Rename</button>
            <button class="thumb-menu-item" @click="downloadItem(index)">Download original</button>
            <div class="thumb-menu-divider"></div>
            <button class="thumb-menu-item is-danger" @click="removeItem(index)">
              <span><font-awesome-icon :icon="['fal', 'trash-can']" class="mr-1" />Delete asset</span>
            </button>
          </template>
          <template #prepend>
            <div class="thumb-card add-card" @click="stayOnQueueView = true">
              <span class="icon has-text-grey">
                <font-awesome-icon :icon="['fal', 'plus']" />
              </span>
              <span class="add-card-label">Add Files</span>
            </div>
          </template>
        </ReviewThumbnailStrip>
        <input
          ref="replaceInputRef"
          type="file"
          class="is-hidden"
          accept="video/*,image/*,audio/*,.pdf"
          @change="onReplaceInputChange"
        />
      </div>

      <!-- Right: Player + Comments stacked -->
      <div class="column review-main">
        <!-- Episode Title & Show Notes authoring view -->
        <EpisodeMetaEditor
          v-if="isEpisodeReview && episodeMetaView"
          :project="episodeProject"
          :reviewId="props.deliverable?.id"
          :fullEpisodeItem="fullEpisodeItem"
        />
        <!-- Episode review with no slot selected yet: prompt instead of an empty viewer -->
        <div v-else-if="isEpisodeReview && !activeItem" class="episode-empty-main">
          <span class="icon has-text-grey-light">
            <font-awesome-icon :icon="['fal', 'clapperboard-play']" />
          </span>
          <p class="has-text-grey">Fill or select an asset slot to start reviewing.</p>
        </div>
        <template v-else>
        <!-- Item header: what you're looking at + which version -->
        <div class="review-item-header">
          <button
            class="rail-toggle"
            :class="{ 'is-off': railCollapsed.assets }"
            :title="railCollapsed.assets ? 'Show assets' : 'Hide assets'"
            :aria-label="railCollapsed.assets ? 'Show assets' : 'Hide assets'"
            @click="toggleRail('assets')"
          >
            <font-awesome-icon :icon="['fal', 'sidebar']" />
          </button>
          <input
            v-if="isRenamingItem"
            ref="renameItemInputRef"
            v-model="renameItemDraft"
            class="input is-small item-rename-input"
            type="text"
            @keyup.enter="commitItemRename"
            @keyup.esc="isRenamingItem = false"
            @blur="commitItemRename"
          >
          <h2 v-else class="item-title" :title="activeMediaItem.name" @dblclick="startItemRename(activeItemIndex)">{{ activeMediaItem.name }}</h2>
          <div v-if="activeItemVersions.length > 1" class="version-group" :class="{ 'has-compare': canCompareVersions }">
          <SearchableSelect
            :modelValue="activeVersionForPanel.id"
            :items="activeItemVersions"
            value-field="id"
            display-field="label"
            :hide-search="true"
            class="item-version-select"
            @update:modelValue="onHeaderVersionSelect"
          >
            <template #item="{ item }">
              <div class="is-flex is-align-items-center is-justify-content-space-between" style="gap: 0.5rem">
                <span class="is-flex is-align-items-center" style="gap: 0.35rem">
                  {{ item.label }}
                  <span v-if="item.clientVersionNumber != null" class="tag is-link is-small">Client v{{ item.clientVersionNumber }}</span>
                </span>
                <span class="is-size-7 has-text-grey">{{ item.date }}</span>
              </div>
            </template>
          </SearchableSelect>
          <button
            v-if="canCompareVersions"
            class="button version-compare-btn"
            title="Compare two versions side by side"
            aria-label="Compare versions"
            @click="openCompare"
          >
            <span class="icon is-small">
              <font-awesome-icon :icon="['fal', 'circles-overlap']" />
            </span>
          </button>
          </div>
          <span v-else class="tag is-info">{{ activeVersionForPanel.label }}</span>
          <span v-if="activeVersionForPanel.clientVersionNumber != null" class="tag is-link">
            Client v{{ activeVersionForPanel.clientVersionNumber }}
          </span>
          <button
            class="rail-toggle is-feedback-toggle"
            :class="{ 'is-off': railCollapsed.feedback }"
            :title="railCollapsed.feedback ? 'Show comments' : 'Hide comments'"
            :aria-label="railCollapsed.feedback ? 'Show comments' : 'Hide comments'"
            @click="toggleRail('feedback')"
          >
            <font-awesome-icon :icon="['fal', 'sidebar-flip']" />
          </button>
        </div>
        <!-- Player area -->
        <div class="player-area-wrapper">
          <div ref="playerAreaRef" class="player-area">
            <div class="viewer-area">
              <ReviewMediaViewer
                :key="activeItem?.id"
                ref="mediaViewerRef"
                :item="activeMediaItem"
                :isPlaying="isPlaying"
                :volume="volume"
                :isMuted="isMuted"
                :playbackRate="playbackRate"
                :crossorigin="activeMediaCrossorigin"
                @timeupdate="onTimeUpdate"
                @play="isPlaying = true"
                @pause="isPlaying = false"
                @duration-change="onDurationChange"
                @refresh-url="onPlayerRefreshUrl"
              />
              <ReviewCommentOverlay
                v-if="activeMediaItem.type === 'video' && showCommentOverlay"
                :comments="activeItemComments"
                :currentTime="currentTime"
              />
            </div>

            <!-- Timeline (video only) -->
            <ReviewTimeline
              v-if="activeMediaItem.type === 'video'"
              :currentTime="currentTime"
              :duration="videoDuration"
              :comments="activeItemComments"
              :isPlaying="isPlaying"
              :activeCommentId="activeCommentId"
              :volume="volume"
              :isMuted="isMuted"
              :showOverlay="showCommentOverlay"
              :isFullscreen="isFullscreen"
              :theaterMode="false"
              hideTheaterToggle
              :playbackRate="playbackRate"
              @seek="seekTo"
              @toggle-playback="togglePlayback"
              @comment-marker-click="seekToComment"
              @update:volume="volume = $event"
              @update:isMuted="isMuted = $event"
              @update:showOverlay="showCommentOverlay = $event"
              @update:playbackRate="playbackRate = $event"
              @toggle-fullscreen="toggleFullscreen"
            >
              <template #extras>
                <button class="overlay-toggle-btn" :class="{ 'is-off': isDownloading }" title="Download original" :disabled="isDownloading" @click="downloadActiveVersion">
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'arrow-down-to-line']" />
                  </span>
                </button>
              </template>
            </ReviewTimeline>
          </div>
        </div>

      </template></div>

      <!-- Right: feedback column — comments, composer, decision, next version -->
      <div v-if="!(isEpisodeReview && (episodeMetaView || !activeItem))" class="column review-comments-col" :class="{ 'is-collapsed': railCollapsed.feedback }">
        <button class="rail-expander" title="Show comments" aria-label="Show comments" @click="toggleRail('feedback')">
          <font-awesome-icon :icon="['fal', 'chevrons-left']" />
          <span class="rail-expander-label">Comments<template v-if="activeItemComments.length"> · {{ activeItemComments.length }}</template></span>
        </button>
        <ReviewCommentPanel
          :comments="commentsForPanel"
          :showAllVersions="showAllVersionComments"
          :itemType="activeMediaItem.type"
          :itemName="activeMediaItem.name"
          :itemStatus="activeItemStatus"
          :hasUnresolvedComments="activeItemHasUnresolvedComments"
          :currentTime="currentTime"
          :isComposing="isComposing"
          :newCommentText="newCommentText"
          :capturedTimestamp="capturedTimestamp"
          :capturedTimestampEnd="capturedTimestampEnd"
          :activeCommentId="activeCommentId"
          :versions="activeItemVersions"
          :activeVersion="activeVersionForPanel"
          :versionUploading="versionUploading"
          :versionReplacing="versionReplacing"
          :versionUploadProgress="versionUploadProgress"
          :versionUploadPaused="versionUploadPaused"
          :statusType="activeDecisionType"
          :isAdmin="true"
          columnLayout
          :stage="activeItemNeedsInternalReapproval ? 'internal_review' : (props.deliverable?.stage || '')"
          :allRevisionsAddressed="allRevisionsAddressed"
          :hasNewVersion="activeItemHasNewVersion"
          :itemNeedsRevision="activeItemNeedsRevision"
          :requiresFinalization="activeItem?.requiresFinalization || false"
          :finalizationNote="activeItem?.finalizationNote || null"
          :finalizationStatus="activeItem?.finalizationStatus || null"
          @rename="renameActiveItem"
          @version-change="switchVersion"
          @comment-click="seekToComment"
          @toggle-resolved="toggleCommentResolved"
          @start-composing="startComposing"
          @set-start-timestamp="setStartTimestamp"
          @set-end-timestamp="setEndTimestamp"
          @clear-timestamps="clearTimestamps"
          @cancel-composing="cancelComposing"
          @update:newCommentText="newCommentText = $event"
          @submit-comment="submitComment"
          @submit-reply="submitReply"
          @edit-comment="editComment"
          @delete-comment="deleteComment"
          @approve="approveItem"
          @request-changes="showNeedsRevisionsConfirm = true"
          @unapprove="unapproveItem"
          @update:showAllVersions="showAllVersionComments = $event"
          @submit-for-review="showSubmitForReviewConfirm = true"
          @update:requiresFinalization="onFinalizationToggle"
          @update:finalizationNote="onFinalizationNoteUpdate"
        />
        <div class="new-version-bar">
          <div v-if="isVersionBusy" class="new-version-progress">
            <progress
              class="progress is-small mb-0"
              :class="versionUploadPaused ? 'is-warning' : 'is-primary'"
              :value="versionUploadProgress >= 100 ? undefined : versionUploadProgress"
              max="100"
            >{{ versionUploadProgress }}%</progress>
            <span class="new-version-progress-label">
              {{ versionReplacing ? 'Replacing file' : `Uploading v${nextVersionNumber}` }} · {{ versionUploadProgress >= 100 ? 'Finalizing…' : `${versionUploadProgress}%` }}
            </span>
            <button
              v-if="versionUploadProgress < 100"
              class="button is-small is-text"
              :title="versionUploadPaused ? 'Resume upload' : 'Pause upload'"
              @click="versionUploadPaused ? resumeVersionUpload() : pauseVersionUpload()"
            >
              <span class="icon is-small">
                <font-awesome-icon :icon="['fas', 'play']" :active-icon="['fas', 'pause']" :active="!versionUploadPaused" />
              </span>
            </button>
          </div>
          <button v-else class="button is-small is-fullwidth new-version-btn" @click="newVersionInputRef?.click()">
            <span class="icon is-small">
              <font-awesome-icon :icon="['fal', 'arrow-up-from-bracket']" />
            </span>
            <span>Upload v{{ nextVersionNumber }} (new version)</span>
          </button>
          <input
            ref="newVersionInputRef"
            type="file"
            class="is-hidden"
            accept="video/*,image/*,audio/*,.pdf"
            @change="onNewVersionInputChange"
          />
        </div>
      </div>
      </div>
    </template>

    <VersionCompareModal
      v-model="showCompare"
      :itemName="activeMediaItem.name"
      :versions="activeItemVersions"
      :initialLeftId="compareInitial.left"
      :initialRightId="compareInitial.right"
      :resolveUrl="resolveCompareUrl"
      :resolvePoster="resolveComparePoster"
      :commentsFor="compareCommentsFor"
      @toggle-resolved="toggleCommentResolved"
    />

    <!-- Debug panel -->
    <div v-if="debugMode" class="debug-panel-wrapper">
      <button class="debug-toggle" @click="debugExpanded = !debugExpanded">
        <span class="icon is-small"><font-awesome-icon :icon="['fal', 'bug']" /></span>
        <span>Debug</span>
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', debugExpanded ? 'chevron-down' : 'chevron-up']" />
        </span>
      </button>
      <div v-if="debugExpanded" class="debug-panel">

        <!-- Preset shortcuts -->
        <div class="debug-presets">
          <span class="debug-presets-label">Presets:</span>
          <button
            class="debug-btn debug-btn-preset"
            :disabled="!!debugApplyingPreset"
            @click="debugApplyPreset('reset')"
          >
            {{ debugApplyingPreset === 'reset' ? 'Applying…' : '0. Reset' }}
          </button>
          <button
            class="debug-btn debug-btn-preset"
            :disabled="!!debugApplyingPreset"
            @click="debugApplyPreset('internal')"
          >
            {{ debugApplyingPreset === 'internal' ? 'Applying…' : '1. Internal Review' }}
          </button>
          <button
            class="debug-btn debug-btn-preset"
            :disabled="!!debugApplyingPreset"
            @click="debugApplyPreset('client')"
          >
            {{ debugApplyingPreset === 'client' ? 'Applying…' : '2. Client Review' }}
          </button>
          <button
            class="debug-btn debug-btn-preset"
            :disabled="!!debugApplyingPreset"
            @click="debugApplyPreset('finalization-complete')"
          >
            {{ debugApplyingPreset === 'finalization-complete' ? 'Applying…' : '3. Finalization Complete' }}
          </button>
        </div>

        <div class="debug-columns">

          <!-- Column 1: Computed State -->
          <div class="debug-col">
            <p class="debug-heading">Computed State</p>
            <div class="debug-row"><span class="debug-label">statusType</span> <code>{{ statusType }}</code></div>
            <div class="debug-row"><span class="debug-label">activeItemStatus</span> <code>{{ activeItemStatus }}</code></div>
            <div class="debug-row"><span class="debug-label">mediaItem.type</span> <code>{{ activeMediaItem.type }}</code></div>
            <div class="debug-row"><span class="debug-label">hasUnresolved</span> <code>{{ activeItemHasUnresolvedComments }}</code></div>
            <div class="debug-row"><span class="debug-label">allRevisionsAddr</span> <code>{{ allRevisionsAddressed }}</code></div>
            <div class="debug-row"><span class="debug-label">hasNewVersion</span> <code>{{ activeItemHasNewVersion }}</code></div>
            <div class="debug-row"><span class="debug-label">needsRevision</span> <code>{{ activeItemNeedsRevision }}</code></div>
            <div class="debug-row"><span class="debug-label">aggInternal</span> <code>{{ aggregateInternalStatus }}</code></div>
            <div class="debug-row"><span class="debug-label">aggClient</span> <code>{{ aggregateClientStatus }}</code></div>

            <p class="debug-heading mt-3">Store State</p>
            <div class="debug-row"><span class="debug-label">loading</span> <code>{{ store.loading }}</code></div>
            <div class="debug-row"><span class="debug-label">initialized</span> <code>{{ store.initialized }}</code></div>
            <div class="debug-row"><span class="debug-label">review.id</span> <code>{{ store.review?.id || 'null' }}</code></div>
            <div class="debug-row"><span class="debug-label">activeItemId</span> <code>{{ store.activeItemId }}</code></div>
            <div class="debug-row"><span class="debug-label">items</span> <code>{{ store.sortedItems.length }}</code></div>
            <div class="debug-row"><span class="debug-label">comments</span> <code>{{ store.comments.length }}</code></div>
            <div class="debug-row"><span class="debug-label">uploadQueue</span> <code>{{ store.uploadQueue.length }}</code></div>

            <p class="debug-heading mt-3">Deliverable Review Fields</p>
            <div class="debug-row"><span class="debug-label">stage</span> <code>{{ props.deliverable?.stage }}</code></div>
            <div class="debug-field">
              <span class="debug-label">intReviewStatus</span>
              <select
                :value="props.deliverable?.internalReviewStatus || ''"
                @change="debugPersistDeliverableReviewFields({ internalReviewStatus: $event.target.value || null, internalReviewComplete: $event.target.value === 'approved' })"
                class="debug-select"
              >
                <option value="">(null)</option>
                <option v-for="s in debugReviewStatuses" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div class="debug-field">
              <span class="debug-label">cliReviewStatus</span>
              <select
                :value="props.deliverable?.clientReviewStatus || ''"
                @change="debugPersistDeliverableReviewFields({ clientReviewStatus: $event.target.value || null, clientReviewComplete: $event.target.value === 'approved' })"
                class="debug-select"
              >
                <option value="">(null)</option>
                <option v-for="s in debugReviewStatuses" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div class="debug-row">
              <span class="debug-label">intComplete</span> <code>{{ props.deliverable?.internalReviewComplete }}</code>
            </div>
            <div class="debug-row">
              <span class="debug-label">cliComplete</span> <code>{{ props.deliverable?.clientReviewComplete }}</code>
            </div>
            <button
              class="debug-btn debug-btn-sync mt-2"
              :disabled="debugDeliverableSaving"
              @click="debugResyncDeliverable"
            >
              {{ debugDeliverableSaving ? 'Syncing...' : 'Resync from Items' }}
            </button>
          </div>

          <!-- Column 2: Per-Item Controls -->
          <div class="debug-col debug-col-wide">
            <p class="debug-heading">Item Controls</p>
            <div v-for="item in store.sortedItems" :key="item.id" class="debug-item-card" :class="{ 'is-active': item.id === store.activeItemId }">
              <div class="debug-item-header" @click="store.selectItem(item.id)">
                <strong>{{ item.name }}</strong>
                <span class="debug-val">{{ item.type }}</span>
              </div>
              <div class="debug-item-controls">
                <div class="debug-field">
                  <span class="debug-label">internal</span>
                  <select
                    :value="item.internalStatus || REVIEW_ITEM_STATUS.PENDING_REVIEW"
                    @change="debugUpdateItemField(item.id, 'internalStatus', $event.target.value)"
                    class="debug-select"
                  >
                    <option v-for="s in debugReviewStatuses" :key="s" :value="s">{{ s }}</option>
                  </select>
                </div>
                <div class="debug-field">
                  <span class="debug-label">client</span>
                  <select
                    :value="item.clientStatus || REVIEW_ITEM_STATUS.PENDING_REVIEW"
                    @change="debugUpdateItemField(item.id, 'clientStatus', $event.target.value)"
                    class="debug-select"
                  >
                    <option v-for="s in debugClientStatuses" :key="s" :value="s">{{ s }}</option>
                  </select>
                </div>
                <div class="debug-field">
                  <span class="debug-label">finalizationStatus</span>
                  <select
                    :value="item.finalizationStatus || ''"
                    @change="debugUpdateItemField(item.id, 'finalizationStatus', $event.target.value || null)"
                    class="debug-select"
                  >
                    <option value="">(null)</option>
                    <option v-for="s in debugFinalizationStatuses" :key="s" :value="s">{{ s }}</option>
                  </select>
                </div>
                <label class="debug-check">
                  <input
                    type="checkbox"
                    :checked="!!item.requiresFinalization"
                    @change="debugUpdateItemField(item.id, 'requiresFinalization', $event.target.checked)"
                  > requiresFinalization
                </label>
                <div class="debug-field">
                  <span class="debug-label">changesReqAt</span>
                  <input
                    type="number"
                    :value="item.changesRequestedAtVersion || ''"
                    @change="debugUpdateItemField(item.id, 'changesRequestedAtVersion', $event.target.value ? Number($event.target.value) : null)"
                    class="debug-input"
                    placeholder="null"
                    min="1"
                  >
                </div>
                <div class="debug-field">
                  <span class="debug-label">currentVer#</span>
                  <input
                    type="number"
                    :value="item.currentVersionNumber || 1"
                    @change="debugUpdateItemField(item.id, 'currentVersionNumber', Number($event.target.value))"
                    class="debug-input"
                    min="1"
                  >
                </div>
              </div>
              <!-- Version details -->
              <div v-if="item.versions?.length" class="debug-versions">
                <div v-for="v in item.versions" :key="v.id" class="debug-version-row">
                  <span class="debug-val">{{ v.label }}</span>
                  <div class="debug-field">
                    <span class="debug-label">clientVer#</span>
                    <input
                      type="number"
                      :value="v.clientVersionNumber ?? ''"
                      @change="debugUpdateVersionField(item.id, v.id, 'clientVersionNumber', $event.target.value ? Number($event.target.value) : null)"
                      class="debug-input"
                      placeholder="null"
                      min="1"
                    >
                  </div>
                  <span class="debug-val" :class="{ 'has-text-success': v.id === item.currentVersionId }">
                    {{ v.id === item.currentVersionId ? 'current' : '' }}
                  </span>
                </div>
              </div>
              <!-- Save + JSON toggle -->
              <div class="debug-item-actions">
                <button
                  v-if="debugDirtyItems[item.id]"
                  class="debug-btn debug-btn-save"
                  :disabled="debugSaving[item.id]"
                  @click="debugPersistItem(item.id)"
                >
                  {{ debugSaving[item.id] ? 'Saving...' : 'Save to Firestore' }}
                </button>
                <button class="debug-json-toggle" @click="debugItemJsonExpanded[item.id] = !debugItemJsonExpanded[item.id]">
                  {{ debugItemJsonExpanded[item.id] ? 'Hide' : 'Show' }} JSON
                </button>
              </div>
              <pre v-if="debugItemJsonExpanded[item.id]" class="debug-json">{{ JSON.stringify(item, null, 2) }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Remove item confirmation -->
    <Teleport v-if="teleportReady" to="body">
      <AppModal
        v-model="showRemoveConfirm"
        title="Remove Item"
        theme="danger"
        confirm-text="Remove"
        size="small"
        :loading="removeLoading"
        @confirm="confirmRemoveItem"
      >
        <p>
          Are you sure you want to remove <strong>{{ removeTargetItem?.name }}</strong>?
          This will delete all versions, files, and comments for this item.
        </p>
      </AppModal>

      <!-- Needs Revisions confirmation -->
      <AppModal
        v-model="showNeedsRevisionsConfirm"
        title="Needs Revisions"
        theme="warning"
        confirm-text="Needs Revisions"
        size="small"
        @confirm="confirmRequestChanges"
      >
        <p>Are you sure you want to mark this asset as needing revisions? This will notify the assigned team member.</p>
      </AppModal>

      <!-- Submit for Review confirmation -->
      <AppModal
        v-model="showSubmitForReviewConfirm"
        title="Submit for Review"
        theme="primary"
        confirm-text="Submit for Review"
        size="small"
        @confirm="confirmResubmitForReview"
      >
        <p>Are you sure you want to resubmit all revised items for another round of review?</p>
      </AppModal>

      <AppModal
        v-model="showReplaceClientVersionConfirm"
        title="Replace Client-Visible File?"
        theme="warning"
        confirm-text="Replace Anyway"
        size="small"
        @confirm="confirmReplaceClientVersion"
      >
        <p v-if="activeItem?.clientStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED">
          The client requested changes on this version. Replacing its file will not count as a revision,
          and their comments will point at the new file. Upload it as a <strong>new version</strong> instead.
        </p>
        <p v-else>
          The client can already see this version. Replacing its file swaps what they see immediately,
          without internal review. Upload it as a <strong>new version</strong> instead.
        </p>
      </AppModal>

    </Teleport>

  </div>
</template>

<script setup>
import BrandLoader from '@/components/global/BrandLoader.vue';
import { ref, shallowRef, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useAdminReviewStore } from '@/stores/adminReviewStore'
import { useAdminProjectsStore } from '@/stores/adminProjectsStore'
import { useUserAuthStore } from '@/stores/userAuthStore'
import { logger } from '@/services/loggerService'
import { toastBridge as toast } from '@/services/toastBridge'
import { BUNNY_URL_INTENT } from '@/utils/bunnyConstants'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { memoryProbe } from '@/services/memoryProbe'
import { attachStallRecovery } from '@/services/uploadStallRecovery'
import {
  DELIVERABLE_STAGES
} from '@/utils/deliverableUtils'
import { REVIEW_ITEM_STATUS, FINALIZATION_STATUS, toMillis, commentAuthorName } from '@/utils/reviewUtils'
import { isBunnyVersion as versionIsBunny } from '@/utils/assetUtils'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import AppModal from '@/components/global/AppModal.vue'
import ReviewMediaViewer from '@/components/client/review/ReviewMediaViewer.vue'
import ReviewTimeline from '@/components/client/review/ReviewTimeline.vue'
import ReviewCommentPanel from '@/components/client/review/ReviewCommentPanel.vue'
import ReviewThumbnailStrip from '@/components/client/review/ReviewThumbnailStrip.vue'
import ReviewCommentOverlay from '@/components/client/review/ReviewCommentOverlay.vue'
import ClientFileDropZone from '@/components/client/upload/ClientFileDropZone.vue'
import EpisodeReviewSlots from '@/components/admin/deliverable/EpisodeReviewSlots.vue'
import EpisodeMetaEditor from '@/components/admin/deliverable/EpisodeMetaEditor.vue'
import VersionCompareModal from '@/components/admin/deliverable/VersionCompareModal.vue'
import DeliverableStatusBar from '@/components/admin/deliverable/DeliverableStatusBar.vue'
import { doc as fsDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/js/firebase'

const props = defineProps({
  deliverable: { type: Object, default: null },
  deliverableForm: { type: Object, default: null },
  company: { type: Object, default: null },
  project: { type: Object, default: null },
  // Whether the review tab is the currently visible tab — the component stays
  // mounted (v-show parent) after first activation, so keyboard nav needs this
  isActive: { type: Boolean, default: true },
  // Assets already promoted to the client library for this deliverable — lets the
  // episode slot rail show a client-approved slot as done instead of empty.
  finalAssets: { type: Array, default: () => [] }
})

const store = useAdminReviewStore()
const projectsStore = useAdminProjectsStore()
const userAuthStore = useUserAuthStore()
const route = useRoute()

// Read the project live from the store so subscribers (and company) reflect
// writes made anywhere — props.project is a snapshot captured when the modal opened.
// Reads `items` directly rather than going through getProjectById, which has a
// non-reactive Map cache that intermittently masks reactive updates after writes.
const liveProject = computed(() => {
  const id = props.project?.id || props.deliverable?.projectId
  if (!id) return props.project || null
  const fromStore = projectsStore.items?.[id]
  return (fromStore && fromStore.id) ? fromStore : (props.project || null)
})

// --- Debug mode ---
const debugMode = computed(() => route.query.debug === 'true')
const debugExpanded = ref(true)
const debugItemJsonExpanded = reactive({})
const debugReviewStatuses = [REVIEW_ITEM_STATUS.PENDING_REVIEW, REVIEW_ITEM_STATUS.APPROVED, REVIEW_ITEM_STATUS.CHANGES_REQUESTED, REVIEW_ITEM_STATUS.IN_REVISION]
const debugClientStatuses = [...debugReviewStatuses, REVIEW_ITEM_STATUS.PENDING_FINALIZATION]
const debugFinalizationStatuses = [FINALIZATION_STATUS.PENDING, FINALIZATION_STATUS.COMPLETE]

function debugUpdateItemField(itemId, field, value) {
  if (!store.items[itemId]) return
  store.items[itemId] = { ...store.items[itemId], [field]: value }
  debugDirtyItems[itemId] = true
  logger.info('[Debug] Updated item field (local)', { itemId, field, value })
}

function debugUpdateVersionField(itemId, versionId, field, value) {
  const item = store.items[itemId]
  if (!item?.versions) return
  const updatedVersions = item.versions.map(v =>
    v.id === versionId ? { ...v, [field]: value } : v
  )
  store.items[itemId] = { ...item, versions: updatedVersions }
  debugDirtyItems[itemId] = true
  logger.info('[Debug] Updated version field (local)', { itemId, versionId, field, value })
}

// Track which items have unsaved debug changes
const debugDirtyItems = reactive({})
const debugSaving = reactive({})

async function debugPersistItem(itemId) {
  const item = store.items[itemId]
  if (!item || !store.review?.id) return
  debugSaving[itemId] = true
  try {
    await store.updateItemFields(itemId, {
      internalStatus: item.internalStatus,
      clientStatus: item.clientStatus,
      internalApprovedBy: item.internalApprovedBy || null,
      internalApprovedAt: item.internalApprovedAt || null,
      clientApprovedBy: item.clientApprovedBy || null,
      clientApprovedAt: item.clientApprovedAt || null,
      changesRequestedAtVersion: item.changesRequestedAtVersion || null,
      currentVersionNumber: item.currentVersionNumber,
      requiresFinalization: !!item.requiresFinalization,
      finalizationStatus: item.finalizationStatus || null,
      versions: item.versions
    })
    delete debugDirtyItems[itemId]
    logger.info('[Debug] Persisted item to Firestore', { itemId })
  } catch (error) {
    logger.error('[Debug] Failed to persist item', { itemId, error: error.message })
  } finally {
    debugSaving[itemId] = false
  }
}

const debugDeliverableSaving = ref(false)

async function debugPersistDeliverableReviewFields(updates) {
  if (!props.deliverable?.id) return
  debugDeliverableSaving.value = true
  try {
    await projectsStore.updateDeliverable(props.deliverable.id, updates)
    logger.info('[Debug] Persisted deliverable review fields', updates)
  } catch (error) {
    logger.error('[Debug] Failed to persist deliverable fields', { error: error.message })
  } finally {
    debugDeliverableSaving.value = false
  }
}

// Track which preset (if any) is being applied; null when idle
const debugApplyingPreset = ref(null)

async function debugApplyPreset(preset) {
  if (!props.deliverable?.id || !store.review?.id) return
  if (debugApplyingPreset.value) return

  debugApplyingPreset.value = preset
  try {
    const userId = userAuthStore.user?.id || null
    const itemList = store.sortedItems

    let deliverableUpdates
    let buildItemUpdates

    if (preset === 'reset') {
      // Pre-review state: nothing approved, no client version numbers assigned
      deliverableUpdates = {
        stage: DELIVERABLE_STAGES.POST_QUEUE,
        internalReviewStatus: null,
        internalReviewComplete: false,
        clientReviewStatus: null,
        clientReviewComplete: false
      }
      buildItemUpdates = (item) => ({
        internalStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        internalApprovedBy: null,
        internalApprovedAt: null,
        clientApprovedBy: null,
        clientApprovedAt: null,
        changesRequestedAtVersion: null,
        versions: (item.versions || []).map(v => ({ ...v, clientVersionNumber: null }))
      })
    } else if (preset === 'internal') {
      // Items are out for internal review — same statuses as reset but stage advances
      deliverableUpdates = {
        stage: DELIVERABLE_STAGES.INTERNAL_REVIEW,
        internalReviewStatus: null,
        internalReviewComplete: false,
        clientReviewStatus: null,
        clientReviewComplete: false
      }
      buildItemUpdates = (item) => ({
        internalStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
        internalApprovedBy: null,
        internalApprovedAt: null,
        clientApprovedBy: null,
        clientApprovedAt: null,
        changesRequestedAtVersion: null,
        versions: (item.versions || []).map(v => ({ ...v, clientVersionNumber: null }))
      })
    } else if (preset === 'client') {
      // Internal approved, ready for client review — mirrors approveItem('internal')
      // by stamping current version with the next clientVersionNumber.
      const now = new Date()
      deliverableUpdates = {
        stage: DELIVERABLE_STAGES.CLIENT_REVIEW,
        internalReviewStatus: REVIEW_ITEM_STATUS.APPROVED,
        internalReviewComplete: true,
        clientReviewStatus: null,
        clientReviewComplete: false
      }
      buildItemUpdates = (item) => {
        const versions = item.versions || []
        const maxClient = versions.reduce(
          (max, v) => (v.clientVersionNumber != null && v.clientVersionNumber > max ? v.clientVersionNumber : max),
          0
        )
        const currentVersion = versions.find(v => v.id === item.currentVersionId)
        const needsClientNumber = currentVersion && currentVersion.clientVersionNumber == null
        const updatedVersions = needsClientNumber
          ? versions.map(v => v.id === item.currentVersionId ? { ...v, clientVersionNumber: maxClient + 1 } : v)
          : versions
        return {
          internalStatus: REVIEW_ITEM_STATUS.APPROVED,
          clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          internalApprovedBy: userId,
          internalApprovedAt: now,
          clientApprovedBy: null,
          clientApprovedAt: null,
          changesRequestedAtVersion: null,
          versions: updatedVersions
        }
      }
    } else if (preset === 'finalization-complete') {
      // Mirrors the end state produced by reviewService.addVersion when an
      // item was pending finalization: internal re-approved, client status
      // reset to pending_review, finalizationStatus flipped to complete,
      // current version assigned a clientVersionNumber.
      const now = new Date()
      deliverableUpdates = {
        stage: DELIVERABLE_STAGES.CLIENT_REVIEW,
        internalReviewStatus: REVIEW_ITEM_STATUS.APPROVED,
        internalReviewComplete: true,
        clientReviewStatus: null,
        clientReviewComplete: false,
        finalizationStatus: FINALIZATION_STATUS.COMPLETE,
        finalizationComplete: true
      }
      buildItemUpdates = (item) => {
        const versions = item.versions || []
        const maxClient = versions.reduce(
          (max, v) => (v.clientVersionNumber != null && v.clientVersionNumber > max ? v.clientVersionNumber : max),
          0
        )
        const currentVersion = versions.find(v => v.id === item.currentVersionId)
        const needsClientNumber = currentVersion && currentVersion.clientVersionNumber == null
        const updatedVersions = needsClientNumber
          ? versions.map(v => v.id === item.currentVersionId ? { ...v, clientVersionNumber: maxClient + 1 } : v)
          : versions
        return {
          internalStatus: REVIEW_ITEM_STATUS.APPROVED,
          clientStatus: REVIEW_ITEM_STATUS.PENDING_REVIEW,
          internalApprovedBy: userId,
          internalApprovedAt: now,
          clientApprovedBy: null,
          clientApprovedAt: null,
          changesRequestedAtVersion: null,
          requiresFinalization: true,
          finalizationStatus: FINALIZATION_STATUS.COMPLETE,
          versions: updatedVersions
        }
      }
    } else {
      return
    }

    await Promise.all(itemList.map(item => store.updateItemFields(item.id, buildItemUpdates(item))))
    await projectsStore.updateDeliverable(props.deliverable.id, deliverableUpdates)
    logger.info('[Debug] Applied preset', { preset, items: itemList.length })
  } catch (error) {
    logger.error('[Debug] Failed to apply preset', { preset, error: error.message })
  } finally {
    debugApplyingPreset.value = null
  }
}

async function debugResyncDeliverable() {
  if (!props.deliverable?.id) return
  debugDeliverableSaving.value = true
  try {
    // Recalculate aggregate from current items
    const allItems = store.sortedItems
    let intStatus = null
    let cliStatus = null

    if (allItems.length > 0) {
      // Internal aggregate
      if (allItems.some(i => i.internalStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED)) intStatus = REVIEW_ITEM_STATUS.CHANGES_REQUESTED
      else if (allItems.some(i => !i.internalStatus || i.internalStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW || i.internalStatus === REVIEW_ITEM_STATUS.IN_REVISION)) intStatus = null
      else intStatus = REVIEW_ITEM_STATUS.APPROVED

      // Client aggregate
      if (allItems.some(i => i.clientStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED)) cliStatus = REVIEW_ITEM_STATUS.CHANGES_REQUESTED
      else if (allItems.some(i => !i.clientStatus || i.clientStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW || i.clientStatus === REVIEW_ITEM_STATUS.IN_REVISION || i.clientStatus === REVIEW_ITEM_STATUS.PENDING_FINALIZATION)) cliStatus = null
      else cliStatus = REVIEW_ITEM_STATUS.APPROVED
    }

    await projectsStore.updateDeliverable(props.deliverable.id, {
      internalReviewStatus: intStatus,
      internalReviewComplete: intStatus === REVIEW_ITEM_STATUS.APPROVED,
      clientReviewStatus: cliStatus,
      clientReviewComplete: cliStatus === REVIEW_ITEM_STATUS.APPROVED
    })
    logger.info('[Debug] Resynced deliverable from items', { internalReviewStatus: intStatus, clientReviewStatus: cliStatus })
  } catch (error) {
    logger.error('[Debug] Failed to resync deliverable', { error: error.message })
  } finally {
    debugDeliverableSaving.value = false
  }
}


// --- Local UI state ---
const stayOnQueueView = ref(false)
const showCommentOverlay = ref(true)
const playerAreaRef = ref(null)
const isFullscreen = ref(false)
const currentTime = ref(0)
const videoDuration = ref(0)
const isPlaying = ref(false)
const volume = ref(1)
const isMuted = ref(false)
const playbackRate = ref(1)
const isComposing = ref(false)
const newCommentText = ref('')
const capturedTimestamp = ref(null)
const capturedTimestampEnd = ref(null)
const activeCommentId = ref(null)
const showAllVersionComments = ref(false)
const mediaViewerRef = ref(null)
const versionUploading = ref(false)
const versionReplacing = ref(false)
const versionUploadProgress = ref(0)
const versionUploadPaused = ref(false)
let activeUploadTask = null
// Stall watchdog for the active version/replace upload (no-op until armed).
let activeStall = { noteProgress: () => {}, cancel: () => {} }
const showNeedsRevisionsConfirm = ref(false)
const showSubmitForReviewConfirm = ref(false)
const showRemoveConfirm = ref(false)
const removeTargetId = ref(null)
const removeLoading = ref(false)
// Review status dropdown state

// Defers <Teleport to="body"> until after initial mount settles — avoids a
// Vue target-resolution race when this tab mounts inside the already-teleported
// deliverable modal (caused `querySelector is not a function` in prod).
const teleportReady = ref(false)

// --- Lifecycle ---
onMounted(async () => {
  await nextTick()
  teleportReady.value = true
  if (props.deliverable?.id) {
    await store.initReview(props.deliverable.id)
  }
  // Hydrate the project into the store if it's not there (e.g. episodes
  // filtered out of the init query, or a stub passed to openDeliverableModal).
  // fetchProjectById is idempotent — returns early if the project is already
  // in items.value.
  const projectId = props.project?.id || props.deliverable?.projectId
  if (projectId && projectsStore.fetchProjectById) {
    try {
      await projectsStore.fetchProjectById(projectId)
    } catch (error) {
      logger.warn('[DeliverableReviewTab] Failed to hydrate project', { projectId, error: error.message })
    }
  }
})

async function retryLoad() {
  store.reset()
  if (props.deliverable?.id) await store.initReview(props.deliverable.id)
}

onBeforeUnmount(() => {
  store.reset()
  Object.values(_previewUrlCache).forEach(url => URL.revokeObjectURL(url))
})

// --- Computed ---
const activeItemIndex = computed(() => {
  if (!store.activeItemId) return 0
  const idx = store.sortedItems.findIndex(i => i.id === store.activeItemId)
  return idx >= 0 ? idx : 0
})

const activeItem = computed(() => store.activeItem)

// Podcast primary-episode deliverables get the fixed slot sidebar instead of
// the free-form upload queue. (project.type === 'episode' guards against a
// stray flag on a non-episode deliverable.)
// Exception: an older primary-flagged deliverable can carry legacy free-form
// items (items with no slot). That isn't a real slot review — showing empty
// slot cards orphans the actual content, so fall back to the free-form UI.
const isEpisodeReview = computed(() => {
  if (!props.deliverable?.isPrimaryEpisodeDeliverable || liveProject.value?.type !== 'episode') return false
  const items = store.sortedItems || []
  if (items.length > 0 && !items.some((i) => i.slot)) return false
  return true
})

// Episode review main area can show the player (a video slot) OR the metadata
// authoring view (Title & Show Notes slot). episodeMetaView toggles between them.
const episodeMetaView = ref(false)
const fullEpisodeItem = computed(() => reviewItems.value.find((i) => i.slot === 'full_episode') || null)

function onEpisodeSelectMedia(index) {
  episodeMetaView.value = false
  selectItem(index)
}
function onEpisodeSelectMeta() {
  episodeMetaView.value = true
}

// Episode reviews: subscribe to the project doc so title / show-notes / thumbnail
// writes (from the meta editor) reflect LIVE in the sidebar + editor without a
// refresh. The store-derived liveProject doesn't reliably pick up direct
// episodeService writes, so this is the authoritative source for both children.
const liveEpisodeProjectDoc = ref(null)
let stopEpisodeProjectWatch = null
watch(
  () => (isEpisodeReview.value ? liveProject.value?.id : null),
  (id) => {
    if (stopEpisodeProjectWatch) { stopEpisodeProjectWatch(); stopEpisodeProjectWatch = null }
    liveEpisodeProjectDoc.value = null
    if (id) {
      stopEpisodeProjectWatch = onSnapshot(fsDoc(db, 'projects', id), (snap) => {
        liveEpisodeProjectDoc.value = snap.exists() ? { id: snap.id, ...snap.data() } : null
      })
    }
  },
  { immediate: true }
)
onBeforeUnmount(() => { if (stopEpisodeProjectWatch) stopEpisodeProjectWatch() })

// Prefer the live-subscribed episode doc; fall back to the store-derived project.
const episodeProject = computed(() => liveEpisodeProjectDoc.value || liveProject.value)


// Stable media item ref that preserves the last valid value during transient null states
// (e.g. snapshot replacement) to prevent v-if toggles that cause DOM patching errors
const activeMediaItem = shallowRef({ id: '', name: '', type: 'image', url: '', poster: '' })

// Cache keyed by versionId + intent + mediaFingerprint (videoId or storage
// path). The fingerprint is what makes "replace file" correct — same
// versionId, new videoId → cache key changes, stale URL is bypassed.
const bunnyMediaCache = ref({})
const bunnyInFlight = new Map()

function bunnyMediaFingerprint(version) {
  const base = version?.bunnyStream?.videoId || version?.bunnyStorage?.path || ''
  // Bunny mints videoId at upload, so it doesn't change when transcoding
  // completes. Without a state suffix, a resolve fired during processing
  // (returns null URL — variants not ready) sets an in-flight promise
  // that a second resolve fired after the status flips to 'ready'
  // piggy-backs on, getting the same stale null result. The video then
  // stays unplayable until a refresh. Suffixing with a playable flag
  // forces a fresh resolve once variants land.
  const variants = version?.bunnyStream?.mp4Variants
  const playable = Array.isArray(variants) && variants.length > 0 ? 'ready' : 'pending'
  // Status too: variants land mid-encode while the server still serves the
  // original, so a resolve in that window must not outlive the flip to 'ready'.
  return `${base}::${playable}::${version?.bunnyStream?.status || ''}`
}

function bunnyCacheKey(reviewId, itemId, versionId, intent, fingerprint = '') {
  return `${reviewId || ''}:${itemId || ''}:${versionId || ''}:${intent}:${fingerprint}`
}

async function resolveBunnyMediaUrl(reviewId, itemId, versionId, intent, fingerprint) {
  const key = bunnyCacheKey(reviewId, itemId, versionId, intent, fingerprint)
  if (bunnyMediaCache.value[key]) return bunnyMediaCache.value[key]
  if (bunnyInFlight.has(key)) return bunnyInFlight.get(key)
  const promise = (async () => {
    try {
      const callable = httpsCallable(getFunctions(), 'getBunnyReviewVideoUrl')
      const { data } = await callable({ reviewId, itemId, versionId, intent })
      const url = data?.url || ''
      if (url) bunnyMediaCache.value = { ...bunnyMediaCache.value, [key]: url }
      return url
    } catch (err) {
      logger.warn('[DeliverableReviewTab] Bunny URL resolution failed', {
        reviewId, itemId, versionId, intent, error: err.message,
      })
      return ''
    } finally {
      bunnyInFlight.delete(key)
    }
  })()
  bunnyInFlight.set(key, promise)
  return promise
}

watch(
  () => {
    const item = activeItem.value
    const version = store.activeVersion
    if (!item || !version || !versionIsBunny(version)) return ''
    const variantCount = Array.isArray(version.bunnyStream?.mp4Variants)
      ? version.bunnyStream.mp4Variants.length : 0
    const status = version.bunnyStream?.status || ''
    const fingerprint = bunnyMediaFingerprint(version)
    return `${props.deliverable?.id || ''}::${item.id}::${version.id}::${status}::${variantCount}::${fingerprint}`
  },
  async (signature) => {
    if (!signature) return
    const item = activeItem.value
    const version = store.activeVersion
    if (!item || !version) return
    const fingerprint = bunnyMediaFingerprint(version)
    await resolveBunnyMediaUrl(props.deliverable?.id, item.id, version.id, 'playback', fingerprint)
  },
  { immediate: true }
)

// Resolve a poster for EVERY item so the slot rail / strip shows thumbnails
// without each item first being clicked. Keyed on versions[0] like reviewItems.
watch(
  () => store.sortedItems.map((item) => {
    const version = item.versions?.[0]
    if (!version || !versionIsBunny(version)) return ''
    const thumbReady = version.bunnyStream?.thumbnailPath || version.bunnyStorage?.path ? '1' : '0'
    return `${item.id}::${version.id}::${thumbReady}::${bunnyMediaFingerprint(version)}`
  }).join('|'),
  () => {
    for (const item of store.sortedItems) {
      const version = item.versions?.[0]
      if (!version || !versionIsBunny(version)) continue
      resolveBunnyMediaUrl(props.deliverable?.id, item.id, version.id, 'thumbnail', bunnyMediaFingerprint(version))
    }
  },
  { immediate: true }
)

// Ensures the active item's poster resolves even if the strip watcher
// hasn't seen the item yet (e.g. just-uploaded, not yet in sortedItems).
watch(
  () => {
    const item = activeItem.value
    const version = store.activeVersion
    if (!item || !version || !versionIsBunny(version)) return ''
    const thumbReady = version.bunnyStream?.thumbnailPath || version.bunnyStorage?.path ? '1' : '0'
    const fingerprint = bunnyMediaFingerprint(version)
    return `${props.deliverable?.id || ''}::${item.id}::${version.id}::${thumbReady}::${fingerprint}`
  },
  async (signature) => {
    if (!signature) return
    const item = activeItem.value
    const version = store.activeVersion
    if (!item || !version) return
    const fingerprint = bunnyMediaFingerprint(version)
    await resolveBunnyMediaUrl(props.deliverable?.id, item.id, version.id, 'thumbnail', fingerprint)
  },
  { immediate: true }
)

// Download the active version's pristine original. Mirrors ReviewDetail's
// download path: legacy Firebase versions carry downloadUrl; Bunny versions get
// a signed /original URL whose filename= param names the saved file.
const isDownloading = ref(false)
async function downloadActiveVersion() {
  const item = activeItem.value
  const version = store.activeVersion
  if (!item || !version) return
  isDownloading.value = true
  try {
    let url = version.downloadUrl || ''
    if (!url && versionIsBunny(version)) {
      url = await resolveBunnyMediaUrl(
        props.deliverable?.id, item.id, version.id,
        BUNNY_URL_INTENT.DOWNLOAD, bunnyMediaFingerprint(version),
      )
    }
    if (!url) { toast.error('No download URL available for this file'); return }
    const link = document.createElement('a')
    link.href = url
    link.download = item.name || version.fileName || 'download'
    link.target = '_blank'
    // noopener only — Bunny hotlink protection 403s any request without a Referer.
    link.rel = 'noopener'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    setTimeout(() => document.body.removeChild(link), 100)
  } finally {
    isDownloading.value = false
  }
}

// Issue 9: the player exhausted its same-URL CDN retries on a Bunny source and
// suspects the signed token expired. Mint a FRESH playback URL (bypassing the
// in-memory cache, which holds the dead one) and write it back into
// bunnyMediaCache — the rebuild watcher below then re-creates activeMediaItem
// with the new url, and the player reloads it restoring playback position.
async function onPlayerRefreshUrl() {
  const item = activeItem.value
  const version = store.activeVersion
  if (!item || !version || !versionIsBunny(version)) return
  try {
    const callable = httpsCallable(getFunctions(), 'getBunnyReviewVideoUrl')
    const { data } = await callable({
      reviewId: props.deliverable?.id,
      itemId: item.id,
      versionId: version.id,
      intent: 'playback',
    })
    const freshUrl = data?.url || ''
    // Drop the result if the user navigated to another clip while we resolved.
    if (!freshUrl || activeItem.value?.id !== item.id) return
    const fingerprint = bunnyMediaFingerprint(version)
    const key = bunnyCacheKey(props.deliverable?.id, item.id, version.id, 'playback', fingerprint)
    bunnyMediaCache.value = { ...bunnyMediaCache.value, [key]: freshUrl }
  } catch (err) {
    logger.warn('[DeliverableReviewTab] Forced Bunny URL re-mint failed', {
      itemId: item.id, versionId: version.id, error: err.message,
    })
  }
}

watch([activeItem, () => store.activeVersion, bunnyMediaCache], ([item, version]) => {
  if (!item) return // Keep last valid value
  const firstVersion = item.versions?.[0]
  // Selected version only — falling back per-field to v1 mixed v1's Bunny
  // status/variants into a version that has none.
  const bunnyStream = (version || firstVersion)?.bunnyStream || null
  const bunnyStorage = (version || firstVersion)?.bunnyStorage || null
  const isBunny = !!(bunnyStream?.videoId || bunnyStorage?.path)
  // Fingerprint binds the cache lookup to the underlying Bunny media — a
  // replace operation swaps videoId and the lookup misses, forcing a fresh
  // resolve and avoiding the "still plays the old video" bug. MUST use
  // bunnyMediaFingerprint (with the ::playable suffix) so this READ key
  // matches the WRITE key in resolveBunnyMediaUrl — otherwise the cached URL
  // is never found once transcoding completes (regression from 0e2c576,
  // which suffixed the write/resolve path here but not this read).
  const fingerprint = isBunny ? bunnyMediaFingerprint(version) : ''

  const cachedBunnyUrl = isBunny
    ? (bunnyMediaCache.value[bunnyCacheKey(props.deliverable?.id, item.id, version?.id, 'playback', fingerprint)] || '')
    : ''
  const cachedBunnyThumb = isBunny
    ? (bunnyMediaCache.value[bunnyCacheKey(props.deliverable?.id, item.id, version?.id, 'thumbnail', fingerprint)] || '')
    : ''
  const url = isBunny ? cachedBunnyUrl : (version?.downloadUrl || '')
  // Poster precedence: legacy version.thumbnailUrl (Firebase-era) →
  // cached Bunny thumbnail (new flow) → null (no poster, shows blank).
  const poster = version?.thumbnailUrl || cachedBunnyThumb || null

  activeMediaItem.value = {
    ...item,
    url,
    poster,
    storagePath: version?.storagePath || null,
    duration: version?.duration || item.versions?.[0]?.duration || null,
    isBunny,
    bunnyStatus: bunnyStream?.status || null,
    hasBunnyVideo: !!bunnyStream?.videoId,
    hasBunnyVariants: Array.isArray(bunnyStream?.mp4Variants) && bunnyStream.mp4Variants.length > 0,
    hasBunnyStorage: !!bunnyStorage?.path,
  }
}, { immediate: true })

// --- Side-by-side version compare (video only) ---
const showCompare = ref(false)
const compareInitial = reactive({ left: null, right: null })
const canCompareVersions = computed(() =>
  activeMediaItem.value?.type === 'video' && (activeItem.value?.versions?.length || 0) > 1
)

function openCompare() {
  const versions = activeItem.value?.versions || []
  const activeIdx = Math.max(0, versions.findIndex(v => v.id === store.activeVersion?.id))
  // versions are newest-first: compare the active version against the one before it
  // (or, when the oldest is active, against the next newer one).
  const prevIdx = activeIdx + 1 < versions.length ? activeIdx + 1 : activeIdx - 1
  compareInitial.left = versions[prevIdx]?.id || null
  compareInitial.right = versions[activeIdx]?.id || null
  isPlaying.value = false
  showCompare.value = true
}

function compareCommentsFor(versionId) {
  const item = activeItem.value
  if (!item) return []
  return store.itemComments(item.id, versionId).filter(c => !c.parentId).map(toPanelComment)
}

async function resolveComparePoster(versionId) {
  const item = activeItem.value
  const version = item?.versions?.find(v => v.id === versionId)
  if (!version) return ''
  if (version.thumbnailUrl) return version.thumbnailUrl
  if (!versionIsBunny(version)) return ''
  return resolveBunnyMediaUrl(props.deliverable?.id, item.id, version.id, 'thumbnail', bunnyMediaFingerprint(version))
}

async function resolveCompareUrl(versionId, { fresh = false } = {}) {
  const item = activeItem.value
  const version = item?.versions?.find(v => v.id === versionId)
  if (!version) return ''
  if (!versionIsBunny(version)) return version.downloadUrl || ''
  const fingerprint = bunnyMediaFingerprint(version)
  if (fresh) {
    // Signed URLs expire (~1h): a retry must mint a new one, not reuse the cache
    const key = bunnyCacheKey(props.deliverable?.id, item.id, version.id, 'playback', fingerprint)
    const { [key]: _expired, ...rest } = bunnyMediaCache.value
    bunnyMediaCache.value = rest
  }
  return resolveBunnyMediaUrl(props.deliverable?.id, item.id, version.id, 'playback', fingerprint)
}

// Bunny CDN doesn't send Access-Control-Allow-Origin — drop crossorigin
// for Bunny URLs; keep 'anonymous' for legacy Firebase URLs.
const activeMediaCrossorigin = computed(() =>
  activeMediaItem.value?.isBunny ? null : 'anonymous'
)

const statusType = computed(() =>
  props.deliverable?.stage === 'client_review' ? 'client' : 'internal'
)

const statusField = computed(() =>
  statusType.value === 'client' ? 'clientStatus' : 'internalStatus'
)

// A new version uploaded during client review waits for internal re-approval
// (reviewService.addVersion) — the admin decides that item internally.
const activeItemNeedsInternalReapproval = computed(() =>
  statusType.value === 'client' && activeItem.value?.internalStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW
)
const activeDecisionType = computed(() =>
  activeItemNeedsInternalReapproval.value ? 'internal' : statusType.value
)

const activeItemStatus = computed(() => {
  const item = activeItem.value
  if (!item) return REVIEW_ITEM_STATUS.PENDING_REVIEW
  const field = activeDecisionType.value === 'client' ? 'clientStatus' : 'internalStatus'
  return item[field] || REVIEW_ITEM_STATUS.PENDING_REVIEW
})

const activeItemVersions = computed(() => {
  const item = activeItem.value
  if (!item?.versions?.length) return [{ id: 'v1', label: 'v1', date: '', clientVersionNumber: null }]
  return item.versions.map(v => ({
    id: v.id,
    label: v.label?.replace(' (Current)', '') || v.label,
    clientVersionNumber: v.clientVersionNumber ?? null,
    date: formatVersionDate(v.createdAt)
  }))
})

const activeVersionForPanel = computed(() => {
  const version = store.activeVersion
  if (!version) return activeItemVersions.value[0]
  return {
    id: version.id,
    label: version.label?.replace(' (Current)', '') || version.label,
    clientVersionNumber: version.clientVersionNumber ?? null,
    date: formatVersionDate(version.createdAt)
  }
})

const currentUserId = computed(() => userAuthStore.user?.id)

const activeItemComments = computed(() => {
  const item = activeItem.value
  const version = store.activeVersion
  if (!item || !version) return []

  return store.itemComments(item.id, version.id).map(toPanelComment)
})

// Shape a raw comment for comment-panel markup (also used by version compare)
function toPanelComment(c) {
  return {
    ...c,
    author: commentAuthorName(c.userName),
    avatarInitials: getInitials(c.userName),
    avatarImage: c.userAvatar || null,
    avatarColor: stringToColor(c.userName || c.userId),
    createdAt: formatRelativeTime(c.createdAt),
    createdAtMs: toMillis(c.createdAt),
    isOwn: c.userId === currentUserId.value
  }
}

const commentsForPanel = computed(() => {
  if (!showAllVersionComments.value) return activeItemComments.value

  const item = activeItem.value
  if (!item) return []

  const versionLabelById = {}
  for (const v of activeItemVersions.value) {
    versionLabelById[v.id] = v.label
  }

  return store.comments
    .filter(c => c.itemId === item.id)
    .map(c => ({
      ...c,
      author: commentAuthorName(c.userName),
      avatarInitials: getInitials(c.userName),
      avatarImage: c.userAvatar || null,
      avatarColor: stringToColor(c.userName || c.userId),
      createdAt: formatRelativeTime(c.createdAt),
      createdAtMs: toMillis(c.createdAt),
      isOwn: c.userId === currentUserId.value,
      versionLabel: versionLabelById[c.versionId] || null
    }))
})

// Reset cross-version visibility when the active item changes
watch(() => activeItem.value?.id, () => {
  showAllVersionComments.value = false
})

// Pick the most meaningful status for sidebar display regardless of deliverable stage
function itemDisplayStatus(item) {
  const client = item.clientStatus
  const internal = item.internalStatus
  // Client-side outcomes take priority when set
  if (client === REVIEW_ITEM_STATUS.APPROVED) return REVIEW_ITEM_STATUS.APPROVED
  if (client === REVIEW_ITEM_STATUS.PENDING_FINALIZATION) return REVIEW_ITEM_STATUS.PENDING_FINALIZATION
  if (client === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return REVIEW_ITEM_STATUS.CHANGES_REQUESTED
  // Fall back to internal status
  if (internal === REVIEW_ITEM_STATUS.APPROVED) return REVIEW_ITEM_STATUS.APPROVED
  if (internal === REVIEW_ITEM_STATUS.CHANGES_REQUESTED) return REVIEW_ITEM_STATUS.CHANGES_REQUESTED
  if (internal === REVIEW_ITEM_STATUS.IN_REVISION) return REVIEW_ITEM_STATUS.IN_REVISION
  return REVIEW_ITEM_STATUS.PENDING_REVIEW
}

const reviewItems = computed(() =>
  store.sortedItems.map(item => {
    const version = item.versions?.[0]
    const isBunny = versionIsBunny(version)
    const reviewIdForBunny = props.deliverable?.id
    const fingerprint = isBunny ? bunnyMediaFingerprint(version) : ''
    const thumbCacheKey = version
      ? bunnyCacheKey(reviewIdForBunny, item.id, version.id, 'thumbnail', fingerprint)
      : ''
    const playbackCacheKey = version
      ? bunnyCacheKey(reviewIdForBunny, item.id, version.id, 'playback', fingerprint)
      : ''
    const cachedThumb = isBunny ? (bunnyMediaCache.value[thumbCacheKey] || '') : ''
    const cachedPlayback = isBunny ? (bunnyMediaCache.value[playbackCacheKey] || '') : ''

    // Bunny videos: poster from Bunny Stream thumbnail. Bunny images: same
    // file URL via Optimizer. Falls through legacy thumbnailUrl/downloadUrl
    // for in-flight Firebase-era versions.
    const url = isBunny ? cachedPlayback : (version?.downloadUrl || '')
    const poster = version?.thumbnailUrl
      || cachedThumb
      || (item.type === 'image' && (version?.downloadUrl || cachedThumb) ? (cachedThumb || version?.downloadUrl) : null)

    const commentCount = store.comments.filter(c => c.itemId === item.id).length

    const versions = item.versions || []
    const versionCount = versions.length
    const promotedVersions = versions.filter(v => v.clientVersionNumber != null)
    const clientVersionCount = promotedVersions.length
    const currentVersion = versions.find(v => v.id === item.currentVersionId)
    const currentClientVersionNumber = currentVersion?.clientVersionNumber ?? null
    const currentVersionPromoted = currentClientVersionNumber != null
    const hasPromotedHistory = clientVersionCount > 0

    return {
      ...item,
      url,
      poster,
      status: itemDisplayStatus(item),
      commentCount,
      duration: version?.duration || null,
      versionCount,
      clientVersionCount,
      currentVersionPromoted,
      currentClientVersionNumber,
      hasPromotedHistory
    }
  })
)

watch(
  () => {
    return store.sortedItems
      .filter(item => {
        const v = item.versions?.[0]
        return v && versionIsBunny(v) && (v.bunnyStream?.thumbnailPath || v.bunnyStorage?.path)
      })
      .map(item => {
        const v = item.versions[0]
        const thumbReady = v.bunnyStream?.thumbnailPath ? '1' : (v.bunnyStorage?.path ? '1' : '0')
        const fp = bunnyMediaFingerprint(v)
        return `${props.deliverable?.id || ''}::${item.id}::${v.id}::${thumbReady}::${fp}`
      })
      .join('|')
  },
  async (signature) => {
    if (!signature) return
    const keys = signature.split('|').filter(Boolean)
    await Promise.all(keys.map((k) => {
      const parts = k.split('::')
      const [reviewId, itemId, versionId, , fingerprint] = parts
      return resolveBunnyMediaUrl(reviewId, itemId, versionId, 'thumbnail', fingerprint)
    }))
  },
  { immediate: true }
)

// Items where the current version isn't visible to the client even though the
// deliverable is in client review — usually because a stage automation rule
// transitioned the deliverable forward without each item passing through the
// per-item internal-approve button. The Cloud Function trigger
// `promoteReviewVersionsTrigger` should auto-resolve this on item write, but
// this banner is a defense-in-depth recovery path if the trigger ever lags.
const frozenClientReviewItems = computed(() => {
  if (props.deliverable?.stage !== 'client_review') return []
  return reviewItems.value.filter(i => !i.currentVersionPromoted && i.hasPromotedHistory)
})

const promotingFrozenItems = ref(false)
async function onPromoteFrozenItems() {
  if (promotingFrozenItems.value) return
  promotingFrozenItems.value = true
  try {
    const { promoted } = await store.promoteUnpromotedItems()
    logger.info('[DeliverableReviewTab] Promoted frozen items', { promoted })
  } finally {
    promotingFrozenItems.value = false
  }
}

const activeItemHasUnresolvedComments = computed(() => {
  const item = activeItem.value
  const version = store.activeVersion
  if (!item || !version) return false
  return store.comments.some(c =>
    c.itemId === item.id &&
    c.versionId === version.id &&
    !c.parentId &&
    !c.resolved
  )
})

const allRevisionsAddressed = computed(() => {
  return !reviewItems.value.some(i =>
    i.internalStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || i.internalStatus === REVIEW_ITEM_STATUS.IN_REVISION ||
    i.clientStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || i.clientStatus === REVIEW_ITEM_STATUS.IN_REVISION
  )
})

const activeItemHasNewVersion = computed(() => {
  const item = activeItem.value
  if (!item) return false
  // If changesRequestedAtVersion is tracked, compare against current version
  if (item.changesRequestedAtVersion) {
    return (item.currentVersionNumber || 1) > item.changesRequestedAtVersion
  }
  // Fallback: if item needs revision but has multiple versions, treat latest as new
  if (activeItemNeedsRevision.value && (item.versions?.length || 0) > 1) {
    return true
  }
  return false
})

const activeItemNeedsRevision = computed(() => {
  const item = activeItem.value
  if (!item) return false
  return item.internalStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || item.internalStatus === REVIEW_ITEM_STATUS.IN_REVISION ||
    item.clientStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED || item.clientStatus === REVIEW_ITEM_STATUS.IN_REVISION
})

// --- Finalization status summary ---
// Local set of item IDs with requiresFinalization for immediate UI feedback
const localFinalizationIds = ref(new Set())
watch(() => store.sortedItems, (items) => {
  const ids = new Set()
  items.forEach(i => { if (i.requiresFinalization) ids.add(i.id) })
  localFinalizationIds.value = ids
}, { immediate: true })

const hasFinalizationItems = computed(() => localFinalizationIds.value.size > 0)
const aggregateFinalizationStatus = computed(() => {
  if (localFinalizationIds.value.size === 0) return null
  const finItems = store.sortedItems.filter(i => localFinalizationIds.value.has(i.id))
  if (finItems.some(i => i.finalizationStatus === FINALIZATION_STATUS.PENDING)) return FINALIZATION_STATUS.PENDING
  if (finItems.length > 0 && finItems.every(i => i.finalizationStatus === FINALIZATION_STATUS.COMPLETE)) return FINALIZATION_STATUS.COMPLETE
  return null // not yet started
})
const finalizationMeta = computed(() => {
  switch (aggregateFinalizationStatus.value) {
    case FINALIZATION_STATUS.PENDING:
      return { label: 'Pending', icon: 'wand-magic-sparkles', cardClass: 'is-warning' }
    case FINALIZATION_STATUS.COMPLETE:
      return { label: 'Complete', icon: 'check-circle', cardClass: 'is-success' }
    default:
      return { label: 'Not Started', icon: 'clock', cardClass: 'is-info' }
  }
})

// --- Computed: upload queue ---
const hasQueuedItems = computed(() =>
  store.uploadQueue.some(q => q.status === 'queued')
)

const queuedCount = computed(() =>
  store.uploadQueue.filter(q => q.status === 'queued').length
)

const queueTotalSize = computed(() => {
  const total = store.uploadQueue.reduce((sum, q) => sum + (q.file?.size || 0), 0)
  return formatFileSize(total)
})

const uploadedCount = computed(() =>
  store.uploadQueue.filter(q => q.status === 'done').length
)

// --- Methods ---
// Fullscreen the bare <video> with native controls (same approach as
// AssetDetail) — the browser's own player fills the screen properly and
// gives a scrubber inside fullscreen. Our custom timeline/comment overlay
// aren't available there; exit to use them.
async function toggleFullscreen() {
  const video = mediaViewerRef.value?.videoElement?.()
  if (!video) return

  if (document.fullscreenElement || document.webkitFullscreenElement) {
    (document.exitFullscreen || document.webkitExitFullscreen)?.call(document)
    return
  }

  if (typeof video.webkitEnterFullscreen === 'function') {
    try {
      video.webkitEnterFullscreen()
      return
    } catch { /* fall through to the standard API */ }
  }

  const requestFn = video.requestFullscreen || video.webkitRequestFullscreen
  if (typeof requestFn !== 'function') return

  video.controls = true
  try {
    await requestFn.call(video)
  } catch (err) {
    video.controls = false
    logger.warn('[DeliverableReviewTab] requestFullscreen failed', { error: err?.message })
  }
}

function onFullscreenChange() {
  isFullscreen.value = !!(document.fullscreenElement || document.webkitFullscreenElement)
  // Strip native controls on exit so our custom timeline drives playback again
  if (!isFullscreen.value) {
    const video = mediaViewerRef.value?.videoElement?.()
    if (video) video.controls = false
  }
}

function onFilesSelected(fileList) {
  const files = Array.from(fileList || [])
  if (!files.length || !props.company?.id) return
  store.enqueueFiles(files)
}

function updateQueueItemFinalization(id, checked) {
  const item = store.uploadQueue.find(q => q.id === id)
  if (item) item.requiresFinalization = checked
}

async function onFinalizationToggle(value) {
  const item = activeItem.value
  if (!item) return
  const newSet = new Set(localFinalizationIds.value)
  if (value) newSet.add(item.id)
  else newSet.delete(item.id)
  localFinalizationIds.value = newSet
  try {
    await store.updateItemFields(item.id, { requiresFinalization: value })
  } catch {
    // Revert local state on failure
    if (value) newSet.delete(item.id)
    else newSet.add(item.id)
    localFinalizationIds.value = new Set(newSet)
  }
}

async function renameActiveItem(name) {
  const item = activeItem.value
  if (!item) return
  await store.updateItemFields(item.id, { name })
}

let _finalizationNoteTimer = null
function onFinalizationNoteUpdate(value) {
  const item = activeItem.value
  if (!item) return
  clearTimeout(_finalizationNoteTimer)
  _finalizationNoteTimer = setTimeout(() => {
    store.updateItemFields(item.id, { finalizationNote: value })
  }, 500)
}

async function handleVersionFileSelected(file) {
  if (!file || !props.company?.id) return
  const item = activeItem.value
  if (!item) return
  try {
    versionUploading.value = true
    versionUploadProgress.value = 0
    versionUploadPaused.value = false
    let lastProgressUpdate = 0
    await store.addVersion(item.id, file, props.company.id, null, (progress) => {
      const now = Date.now()
      const rounded = Math.round(progress)
      // Feed the watchdog on every event (unthrottled) so it sees real progress.
      activeStall.noteProgress(rounded)
      // Throttle progress updates to ~4/sec to avoid concurrent reactive cascades
      if (rounded >= 100 || now - lastProgressUpdate >= 250) {
        versionUploadProgress.value = rounded
        lastProgressUpdate = now
      }
    }, (task) => {
      activeUploadTask = task
      activeStall = attachStallRecovery(task, {
        isActive: () => versionUploading.value && !versionUploadPaused.value,
        label: '[DeliverableReviewTab] version upload'
      })
    })
  } catch (_err) {
    // Service already warned + toasted this one; don't double-log it as an error.
    if (_err?.message === 'Item not found') {
      logger.warn('[DeliverableReviewTab] Upload target was removed', { itemId: item.id })
    } else {
      logger.error('[DeliverableReviewTab] Version upload failed', _err)
    }
  } finally {
    activeStall.cancel()
    activeStall = { noteProgress: () => {}, cancel: () => {} }
    // Let snapshot-triggered re-renders flush before clearing upload state
    await nextTick()
    versionUploading.value = false
    versionUploadProgress.value = 0
    versionUploadPaused.value = false
    activeUploadTask = null
  }
}

async function handleReplaceFileSelected(file) {
  if (!file || !props.company?.id) return
  const item = activeItem.value
  if (!item) return
  const versionId = item.currentVersionId
  if (!versionId) return
  try {
    versionReplacing.value = true
    versionUploadProgress.value = 0
    versionUploadPaused.value = false
    let lastProgressUpdate = 0
    await store.replaceVersionFile(item.id, versionId, file, props.company.id, (progress) => {
      const now = Date.now()
      const rounded = Math.round(progress)
      activeStall.noteProgress(rounded)
      if (rounded >= 100 || now - lastProgressUpdate >= 250) {
        versionUploadProgress.value = rounded
        lastProgressUpdate = now
      }
    }, (task) => {
      activeUploadTask = task
      activeStall = attachStallRecovery(task, {
        isActive: () => versionReplacing.value && !versionUploadPaused.value,
        label: '[DeliverableReviewTab] version replace'
      })
    })
  } catch (_err) {
    // Service already warned + toasted this one; don't double-log it as an error.
    if (_err?.message === 'Item not found') {
      logger.warn('[DeliverableReviewTab] Replace target was removed', { itemId: item.id })
    } else {
      logger.error('[DeliverableReviewTab] Version file replace failed', _err)
    }
  } finally {
    activeStall.cancel()
    activeStall = { noteProgress: () => {}, cancel: () => {} }
    await nextTick()
    versionReplacing.value = false
    versionUploadProgress.value = 0
    versionUploadPaused.value = false
    activeUploadTask = null
  }
}

function pauseVersionUpload() {
  if (activeUploadTask && activeUploadTask.pause()) {
    versionUploadPaused.value = true
  }
}

function resumeVersionUpload() {
  if (activeUploadTask && activeUploadTask.resume()) {
    versionUploadPaused.value = false
  }
}

async function startQueueUpload() {
  stayOnQueueView.value = true
  memoryProbe.log('[DeliverableReviewTab] upload start', {
    queued: store.uploadQueue.filter(q => q.status === 'queued').length,
    videos: store.uploadQueue.filter(q => q.type === 'video').length,
  })
  await store.processUploadQueue(
    props.deliverable.id,
    props.project?.id || props.deliverable.projectId,
    props.company.id
  )
  memoryProbe.log('[DeliverableReviewTab] upload complete', {
    done: store.uploadQueue.filter(q => q.status === 'done').length,
    errors: store.uploadQueue.filter(q => q.status === 'error').length,
  })
  setTimeout(() => {
    store.clearCompletedFromQueue()
    // Keep the queue view open if any items failed, so the error + Retry stay visible.
    const hasErrors = store.uploadQueue.some(q => q.status === 'error')
    if (!hasErrors) stayOnQueueView.value = false
  }, 3000)
}

function retryQueueUpload(id) {
  // Re-arm the failed item, then re-run the queue (guarded by isUploading + the
  // disabled Retry button, so this can't fire mid-batch).
  store.retryQueueItem(id)
  startQueueUpload()
}

function queueItemIcon(type) {
  if (type === 'video') return ['fadl', 'film']
  if (type === 'image') return ['fadl', 'image']
  if (type === 'audio') return ['fadl', 'music']
  return ['fadl', 'file']
}

function switchVersion(version) {
  const item = activeItem.value
  if (!item) return
  store.selectVersion(item.id, version.id)
  activeCommentId.value = null
  isPlaying.value = false
  currentTime.value = 0
}

function removeItem(index) {
  // Resolve the index to an id NOW, not at confirm time. sortedItems is live —
  // a concurrent delete, a finished upload, or another user's sortOrder change
  // shifts it while the dialog is open, and a held position would then delete
  // whatever slid into that slot instead of what the user clicked.
  removeTargetId.value = store.sortedItems[index]?.id || null
  showRemoveConfirm.value = true
}

const removeTargetItem = computed(() => {
  if (!removeTargetId.value) return null
  return store.sortedItems.find(i => i.id === removeTargetId.value) || null
})

async function confirmRemoveItem() {
  const item = removeTargetItem.value
  if (!item || !props.company?.id) return
  try {
    removeLoading.value = true
    await store.deleteItem(item.id, props.company.id)
    showRemoveConfirm.value = false
    removeTargetId.value = null
  } catch {
    // Error handled in store/service
  } finally {
    removeLoading.value = false
  }
}

// --- File actions (asset card ⋯ menu) + new version (feedback column) ---
// Replace lives on the asset card, away from "new version". Select the item
// first so the progress bar under its feedback column belongs to it.
// --- Collapsible rails (per-viewer, remembered in this browser) ---
const RAIL_PREF_KEY = 'reviewTab.collapsedRails'
function readRailPrefs() {
  try {
    return { assets: false, feedback: false, ...JSON.parse(localStorage.getItem(RAIL_PREF_KEY) || '{}') }
  } catch {
    return { assets: false, feedback: false }
  }
}
const railCollapsed = reactive(readRailPrefs())
function toggleRail(rail) {
  railCollapsed[rail] = !railCollapsed[rail]
  try {
    localStorage.setItem(RAIL_PREF_KEY, JSON.stringify(railCollapsed))
  } catch {
    // storage blocked: collapse still works for this session
  }
}

// DeliverableModal renders this target in its tab row
const hasToolbarSlot = !!document.getElementById('deliverable-toolbar-extras')
const isVersionBusy = computed(() => versionUploading.value || versionReplacing.value)
const nextVersionNumber = computed(() => (activeItem.value?.versions?.length || 0) + 1)
const replaceInputRef = ref(null)
const newVersionInputRef = ref(null)

function selectAnyItem(index) {
  if (isEpisodeReview.value) onEpisodeSelectMedia(index)
  else selectItem(index)
}

function startReplace(index) {
  if (isVersionBusy.value) {
    toast.info('Wait for the current upload to finish')
    return
  }
  selectAnyItem(index)
  const item = activeItem.value
  const current = item?.versions?.find(v => v.id === item.currentVersionId)
  if (current?.clientVersionNumber != null) {
    showReplaceClientVersionConfirm.value = true
    return
  }
  replaceInputRef.value?.click()
}
const showReplaceClientVersionConfirm = ref(false)
function confirmReplaceClientVersion() {
  showReplaceClientVersionConfirm.value = false
  replaceInputRef.value?.click()
}
function onReplaceInputChange(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (file) handleReplaceFileSelected(file)
}
function onNewVersionInputChange(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (file) handleVersionFileSelected(file)
}

function downloadItem(index) {
  selectAnyItem(index)
  downloadActiveVersion()
}

function onHeaderVersionSelect(versionId) {
  if (versionId && versionId !== store.activeVersion?.id) switchVersion({ id: versionId })
}

const isRenamingItem = ref(false)
const renameItemDraft = ref('')
const renameItemInputRef = ref(null)
async function startItemRename(index) {
  selectAnyItem(index)
  renameItemDraft.value = activeItem.value?.name || ''
  isRenamingItem.value = true
  await nextTick()
  renameItemInputRef.value?.select()
}
function commitItemRename() {
  if (!isRenamingItem.value) return
  isRenamingItem.value = false
  const name = renameItemDraft.value.trim()
  if (name && name !== activeItem.value?.name) renameActiveItem(name)
}

function selectItem(index) {
  if (index === activeItemIndex.value) return
  isPlaying.value = false
  currentTime.value = 0
  videoDuration.value = 0
  activeCommentId.value = null
  cancelComposing()
  const item = store.sortedItems[index]
  if (item) store.selectItem(item.id)
}

// --- Keyboard navigation: ↑/↓ move between assets while the review tab is visible ---
const handleAssetArrowNav = (e) => {
  // Option/Alt + ↑/↓ only
  if (
    (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') ||
    !e.altKey || e.metaKey || e.ctrlKey || e.shiftKey
  ) {
    return
  }
  if (!props.isActive) return

  // A stacked modal (confirm dialog, upload overlay, etc.) is open on top of
  // the deliverable modal — which itself accounts for one .modal.is-active
  if (document.querySelectorAll('.modal.is-active').length > 1) return

  // Don't hijack typing, native controls, or the video player's own arrow keys
  const target = e.target
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable ||
      target.closest?.('.video-js'))
  ) {
    return
  }

  const count = store.sortedItems.length
  if (count === 0) return
  const next = activeItemIndex.value + (e.key === 'ArrowDown' ? 1 : -1)
  if (next < 0 || next >= count) return

  e.preventDefault()
  selectItem(next)
}

onMounted(() => {
  document.addEventListener('keydown', handleAssetArrowNav)
  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('webkitfullscreenchange', onFullscreenChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleAssetArrowNav)
  document.removeEventListener('fullscreenchange', onFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
})

function onTimeUpdate(time) {
  currentTime.value = time
}

function onDurationChange(duration) {
  // Ignore non-usable values so a late Infinity/NaN can't clobber a good
  // duration (e.g. one already seeded from the stored upload-time value).
  if (!Number.isFinite(duration) || duration <= 0) return
  videoDuration.value = duration
}

function seekTo(time) {
  currentTime.value = time
  if (mediaViewerRef.value) {
    mediaViewerRef.value.seekTo(time)
  }
}

function togglePlayback() {
  isPlaying.value = !isPlaying.value
}

function seekToComment(comment) {
  activeCommentId.value = comment.id
  if (comment.timestamp != null) {
    seekTo(comment.timestamp)
    setTimeout(() => {
      playerAreaRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }
}

function startComposing() {
  isPlaying.value = false
  isComposing.value = true
}

function setStartTimestamp() {
  capturedTimestamp.value = currentTime.value
}

function setEndTimestamp() {
  capturedTimestampEnd.value = currentTime.value
}

function clearTimestamps() {
  capturedTimestamp.value = null
  capturedTimestampEnd.value = null
}

function cancelComposing() {
  isComposing.value = false
  newCommentText.value = ''
  capturedTimestamp.value = null
  capturedTimestampEnd.value = null
}

async function submitComment() {
  if (!newCommentText.value.trim()) return
  const item = activeItem.value
  const version = store.activeVersion
  if (!item || !version) return

  try {
    const result = await store.addComment({
      itemId: item.id,
      versionId: version.id,
      text: newCommentText.value.trim(),
      timestamp: activeMediaItem.value.type === 'video' ? (capturedTimestamp.value ?? currentTime.value) : null,
      timestampEnd: capturedTimestampEnd.value
    })
    if (result?.id) {
      activeCommentId.value = result.id
    }
    cancelComposing()
  } catch {
    // Error handled in store/service
  }
}

async function submitReply(payload) {
  try {
    await store.addReply(payload.parentCommentId, payload.text)
  } catch {
    // Error handled in store/service
  }
}

async function editComment(updated) {
  try {
    await store.updateComment(updated.id, { text: updated.text })
  } catch {
    // Error handled in store/service
  }
}

async function deleteComment(comment) {
  try {
    await store.deleteComment(comment.id)
    if (activeCommentId.value === comment.id) {
      activeCommentId.value = null
    }
  } catch {
    // Error handled in store/service
  }
}

async function toggleCommentResolved(comment) {
  try {
    await store.toggleResolved(comment.id, !comment.resolved)
  } catch {
    // Error handled in store/service
  }
}

function advanceToNextItem() {
  const items = store.sortedItems
  const currentIdx = activeItemIndex.value
  for (let offset = 1; offset < items.length; offset++) {
    const idx = (currentIdx + offset) % items.length
    const status = items[idx][statusField.value]
    if (status === REVIEW_ITEM_STATUS.PENDING_REVIEW) {
      selectItem(idx)
      return
    }
  }
}

async function approveItem() {
  const item = activeItem.value
  if (!item) return
  try {
    await store.approveItem(item.id, activeDecisionType.value)
    advanceToNextItem()
  } catch {
    // Error handled in store/service
  }
}

function confirmRequestChanges() {
  showNeedsRevisionsConfirm.value = false
  requestItemChanges()
}

function confirmResubmitForReview() {
  showSubmitForReviewConfirm.value = false
  resubmitForReview()
}

async function requestItemChanges() {
  const item = activeItem.value
  if (!item) return
  try {
    await store.requestChanges(item.id, activeDecisionType.value)
    advanceToNextItem()
  } catch {
    // Error handled in store/service
  }
}

async function unapproveItem() {
  const item = activeItem.value
  if (!item) return
  try {
    await store.resetStatus(item.id, statusType.value)
  } catch {
    // Error handled in store/service
  }
}

async function resubmitForReview() {
  try {
    // Always resubmit for internal review first — the team needs to re-review the new version
    // before it goes back to the client. Client changes_requested stays until client re-reviews.
    await store.resubmitForReview('internal')
  } catch {
    // Error handled in store/service
  }
}

// --- Helpers ---
function formatFileSize(bytes) {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let size = bytes
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024
    i++
  }
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

const _previewUrlCache = {}
function getQueueItemPreview(qItem) {
  if (!_previewUrlCache[qItem.id]) {
    _previewUrlCache[qItem.id] = URL.createObjectURL(qItem.file)
  }
  return _previewUrlCache[qItem.id]
}

function getInitials(name) {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

function stringToColor(str) {
  if (!str) return '#7a7a7a'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const colors = ['#667eea', '#f5576c', '#48c78e', '#485fc7', '#ff6b6b', '#ffd93d', '#6c5ce7', '#00b894']
  return colors[Math.abs(hash) % colors.length]
}

function formatRelativeTime(timestamp) {
  if (!timestamp) return ''
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatVersionDate(timestamp) {
  if (!timestamp) return ''
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Expose for debugging
logger.info('[DeliverableReviewTab] Mounted', { deliverableId: props.deliverable?.id })
</script>

<style lang="scss" scoped>
.review-tab {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

// Review-only status (Finalization, Skip Internal Review), teleported into
// the modal's tab row beside DeliverableStatusBar
.review-tab-actions {
  display: flex;
  align-items: flex-end;
  gap: 1rem;

  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;

    .label {
      margin-bottom: 0;
      font-size: 0.6rem;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bulma-text-weak, #6b7280);
    }
  }
}

.empty-state {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.review-columns {
  flex: 1;
  min-height: 0;
}

.frozen-client-banner {
  margin: 0.75rem 1rem;
  padding: 0.75rem 1rem;
  flex-shrink: 0;
}

.thumbnail-sidebar {
  overflow-y: auto;
  border-right: 1px solid var(--bulma-border);

  &.is-narrow-rail {
    flex: 0 0 240px;
    max-width: 240px;
  }
}

.add-card {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  border: 2px dashed var(--bulma-border, #ddd);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  background: transparent;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--bulma-primary, #485fc7);
    background: rgba(72, 95, 199, 0.04);
  }
}

.add-card-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--bulma-grey, #7a7a7a);
}

.review-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.episode-empty-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  padding: 2rem;
  text-align: center;

  .icon {
    font-size: 3rem;
  }
  p {
    font-size: 1.05rem;
    max-width: 22rem;
  }
}

// Player fills the column: black stage takes all spare height, controls pin
// to the bottom, and the video letterboxes inside (so vertical cuts use the
// full height instead of a 16:9 box).
.player-area-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.player-area {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--bulma-scheme-main, #fff);
}

.viewer-area {
  position: relative;
  flex: 1;
  min-height: 240px;
  background: #000;

  :deep(.review-media-viewer) {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
  }

  :deep(.media-container) {
    flex: 1;
    min-height: 0;
    width: 100%;
    // parent passes an inline aspect-ratio; the stage decides size here
    aspect-ratio: auto !important;
    border-radius: 0;
  }
}

// Timeline layout overrides for modal context
:deep(.review-timeline) {
  padding: 0.35rem 0.5rem;
  gap: 0.15rem;

  .time-row {
    text-align: left;
    padding-left: 0.25rem;
  }

  .controls-row {
    align-items: center;
    gap: 0.5rem;
  }
}

// Right column: comments scroll, composer + decision + new version pin to the bottom
// --- Collapsible rails ---
.rail-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--bulma-text, #4a4a4a);
  cursor: pointer;

  &:hover {
    background: var(--bulma-scheme-main-ter, #f0f0f0);
  }

  &.is-off {
    color: var(--bulma-grey-light, #b5b5b5);
  }

  &.is-feedback-toggle {
    margin-left: auto;
  }
}

// Collapsed rail = a thin strip that reopens on click
.rail-expander {
  display: none;
}

.thumbnail-sidebar.is-collapsed,
.review-comments-col.is-collapsed {
  flex: 0 0 36px;
  max-width: 36px;
  width: 36px;
  overflow: hidden;

  > :not(.rail-expander) {
    display: none !important;
  }

  .rail-expander {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    height: 100%;
    padding: 0.85rem 0;
    border: none;
    background: none;
    color: var(--bulma-text-weak, #6b7280);
    cursor: pointer;

    &:hover {
      background: var(--bulma-scheme-main-bis, #f5f5f5);
      color: var(--bulma-text-strong, #363636);
    }
  }

  .rail-expander-label {
    writing-mode: vertical-rl;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }
}

// Under 1400px the feedback column drops below the player; the right side
// scrolls as one, the asset rail stays put.
@media screen and (max-width: 1399px) {
  .review-columns {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-content: start;
    overflow-y: auto;
    // lets the sticky rail size itself to this scroller (100cqh)
    container-type: size;
  }

  .thumbnail-sidebar {
    grid-column: 1;
    grid-row: 1 / span 2;
    position: sticky;
    top: 0;
    align-self: start;
    height: 100cqh;
    width: 240px;

    &.is-one-quarter {
      width: 320px;
    }
  }

  .review-main {
    grid-column: 2;
    overflow: visible;
  }

  // Column scrolls here, so the stage needs its own height
  .viewer-area {
    flex: none;
    height: 60vh;
  }

  .review-comments-col.review-comments-col {
    grid-column: 2;
    max-width: none;
    border-left: none;
    border-top: 1px solid var(--bulma-border);

    // Upload sits with the actions, not after a long comment list
    .new-version-bar {
      order: -1;
      padding-top: 0.75rem;
    }

    // Stacked under the video there's nothing to collapse into
    &.is-collapsed {
      flex: none;
      max-width: none;
      width: auto;

      > :not(.rail-expander) {
        display: flex !important;
      }

      .rail-expander {
        display: none;
      }
    }
  }

  .rail-toggle.is-feedback-toggle {
    display: none;
  }

  .thumbnail-sidebar.is-collapsed {
    width: 36px;
  }
}

// Tablet: slimmer rail, asset name gets its own line in the header
@media screen and (max-width: 1023px) {
  .thumbnail-sidebar.is-one-quarter:not(.is-collapsed) {
    width: 260px;
  }

  .review-item-header {
    flex-wrap: wrap;
    row-gap: 0.4rem;
  }

  .item-title,
  .item-rename-input {
    order: -1;
    flex: 1 1 100%;
    max-width: none;
  }
}

// Phone: one column, rail on top with its own scroll
@media screen and (max-width: 699px) {
  .review-columns {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
  }

  .thumbnail-sidebar,
  .thumbnail-sidebar.is-one-quarter:not(.is-collapsed) {
    grid-column: 1;
    grid-row: auto;
    position: static;
    width: auto;
    // explicit height: a scroll container's auto height contributes ~0 to the row
    height: 40vh;
    border-right: none;
    border-bottom: 1px solid var(--bulma-border);
  }

  .thumbnail-sidebar.is-collapsed {
    width: auto;
    max-width: none;
    height: 36px;

    .rail-expander {
      flex-direction: row;
      justify-content: center;
      padding: 0;
    }

    .rail-expander-label {
      writing-mode: horizontal-tb;
    }
  }

  .review-main,
  .review-comments-col.review-comments-col {
    grid-column: 1;
  }
}

.review-comments-col {
  flex: 0 0 380px;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid var(--bulma-border);

  :deep(.review-comment-panel) {
    min-height: 0;
  }
}

.new-version-bar {
  flex-shrink: 0;
  padding: 0 1rem 0.75rem;
}

.new-version-btn {
  border-style: dashed;
  color: var(--bulma-text-weak, #6b7280);
}

.new-version-progress {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .progress {
    flex: 1;
  }
}

.new-version-progress-label {
  font-size: 0.75rem;
  color: var(--bulma-text-weak, #6b7280);
  white-space: nowrap;
}

.review-item-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0.25rem;
  position: relative;
  z-index: 10;
}

.item-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: text;
}

.item-rename-input {
  max-width: 360px;
}

// Version select + compare read as one control
.version-group {
  display: flex;
  flex-shrink: 0;

  &.has-compare .item-version-select :deep(.select-button) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.version-compare-btn {
  height: 2.5em;
  margin-left: -1px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.item-version-select {
  width: 190px;
  flex-shrink: 0;
}


// Upload queue stage — same centering as empty state, same width
.upload-queue-stage {
  width: 100%;
  max-width: 600px;
  padding: 1rem;
  max-height: 100%;
  overflow-y: auto;

  // Compact the drop zone — keeps the dashed border language, just shorter
  :deep(.compact-drop-zone.file-drop-zone) {
    padding: 0.75rem 1.25rem;
    border-radius: 10px;

    .drop-zone-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .drop-zone-icon {
      margin-bottom: 0;

      .icon.is-large {
        font-size: 1.1rem;
        width: auto;
        height: auto;
      }
    }

    .drop-zone-title {
      font-size: 0.85rem;
      font-weight: 500;
      margin-bottom: 0;
    }

    .drop-zone-subtitle,
    .drop-zone-hint {
      display: none;
    }
  }
}

.queue-file-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.queue-file-item {
  padding: 0.75rem;
  border: 1px solid var(--bulma-border);
  border-radius: 10px;
  background: var(--bulma-scheme-main);
  transition: border-color 0.2s ease;
  position: relative;
  z-index: 1;

  &.is-uploading {
    border-color: var(--bulma-primary);
  }

  &.is-done {
    border-color: var(--bulma-success);
  }

  &.is-error {
    border-color: var(--bulma-danger);
  }

  // Card-as-progress-bar background
  .queue-progress-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    z-index: -1;
    height: 100%;
    background-color: transparent;
    transition: all 0.2s ease;

    .progress-fill {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      transition: width 0.3s ease;
      border-radius: 10px;
    }

    &.is-primary .progress-fill {
      background-color: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.12);
    }

    &.is-success .progress-fill {
      background-color: hsla(var(--bulma-success-h), var(--bulma-success-s), var(--bulma-success-l), 0.12);
    }

    &.is-danger .progress-fill {
      background-color: hsla(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l), 0.12);
    }

    &.is-warning .progress-fill {
      background-color: hsla(var(--bulma-warning-h), var(--bulma-warning-s), var(--bulma-warning-l), 0.16);
    }
  }

  .queue-title-input {
    box-shadow: none !important;
    padding: 0.25rem 0.5rem !important;
    height: auto !important;
    font-size: 0.85rem !important;
    background: var(--bulma-scheme-main-bis) !important;
    border: 1px solid var(--bulma-border) !important;
    border-radius: 6px !important;

    &:focus {
      border-color: var(--bulma-primary) !important;
      background: var(--bulma-scheme-main) !important;
    }
  }
}

.queue-item-thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  font-size: 1rem;
  background: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.08);
}

.queue-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.queue-meta-tag {
  display: inline-block;
  font-size: 0.65rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.03em;
  padding: 0.1em 0.5em;
  border-radius: 4px;
  border: 1px solid var(--bulma-border);
  background: var(--bulma-scheme-main-bis);
  color: var(--bulma-grey);
  line-height: 1.4;
}

.queue-item-action {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.drop-zone-wrapper {
  width: 100%;
  max-width: 600px;
  padding: 1rem;
}

.has-text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// --- Debug Panel ---
.debug-panel-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none;
}

.debug-toggle {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  position: absolute;
  bottom: 100%;
  right: 12px;
  padding: 0.2rem 0.6rem;
  background: rgba(30, 30, 30, 0.92);
  color: #0f0;
  border: 1px solid #333;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  font-size: 0.7rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  cursor: pointer;

  &:hover { background: rgba(50, 50, 50, 0.95); }
}

.debug-panel {
  pointer-events: auto;
  background: rgba(20, 20, 20, 0.94);
  color: #ccc;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.68rem;
  line-height: 1.5;
  border-top: 1px solid #333;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.5rem;
}

.debug-columns {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.debug-col {
  flex: 0 0 220px;
  min-width: 0;
}

.debug-col-wide {
  flex: 1 1 auto;
  min-width: 280px;
}

.debug-heading {
  color: #0f0;
  font-weight: 700;
  font-size: 0.72rem;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #333;
  padding-bottom: 0.15rem;
}

.debug-check {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  padding: 0.1rem 0;

  input { accent-color: #0f0; }
}

.debug-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.1rem 0;
  gap: 0.5rem;

  code {
    color: #ff0;
    background: none;
    padding: 0;
    font-size: inherit;
  }
}

.debug-field {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.1rem 0;
}

.debug-label {
  color: #888;
  font-size: 0.65rem;
  flex-shrink: 0;
}

.debug-val {
  color: #aaa;
  font-size: 0.65rem;
}

.debug-input {
  background: #111;
  color: #fff;
  border: 1px solid #444;
  border-radius: 2px;
  padding: 0.1rem 0.3rem;
  font-size: 0.65rem;
  font-family: inherit;
  width: 60px;

  &:focus { border-color: #0f0; outline: none; }
}

.debug-select {
  background: #111;
  color: #fff;
  border: 1px solid #444;
  border-radius: 2px;
  padding: 0.1rem 0.2rem;
  font-size: 0.65rem;
  font-family: inherit;

  &:focus { border-color: #0f0; outline: none; }
}

.debug-range {
  width: 80px;
  accent-color: #0f0;
}

.debug-item-card {
  background: rgba(40, 40, 40, 0.8);
  border: 1px solid #333;
  border-radius: 4px;
  padding: 0.4rem;
  margin-bottom: 0.4rem;

  &.is-active {
    border-color: #0f0;
  }
}

.debug-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0.3rem;

  strong {
    color: #fff;
    font-size: 0.7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.debug-item-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
}

.debug-versions {
  margin-top: 0.3rem;
  padding-top: 0.3rem;
  border-top: 1px solid #333;
}

.debug-version-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.1rem 0;
}

.debug-item-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.debug-btn {
  font-family: inherit;
  font-size: 0.62rem;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.debug-btn-save {
  background: rgba(0, 200, 80, 0.15);
  color: #0c6;
  border-color: #0c6;

  &:hover:not(:disabled) { background: rgba(0, 200, 80, 0.3); }
}

.debug-presets {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #333;
}

.debug-presets-label {
  color: #0f0;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 0.25rem;
}

.debug-btn-preset {
  background: rgba(255, 170, 0, 0.15);
  color: #fa0;
  border-color: #fa0;

  &:hover:not(:disabled) { background: rgba(255, 170, 0, 0.3); }
}

.debug-btn-sync {
  background: rgba(0, 160, 255, 0.15);
  color: #0af;
  border-color: #0af;
  width: 100%;

  &:hover:not(:disabled) { background: rgba(0, 160, 255, 0.3); }
}

.debug-json-toggle {
  background: none;
  border: none;
  color: #0af;
  cursor: pointer;
  font-size: 0.6rem;
  font-family: inherit;
  padding: 0.2rem 0;
  text-decoration: underline;

  &:hover { color: #4cf; }
}

.debug-json {
  background: #111;
  color: #999;
  font-size: 0.6rem;
  padding: 0.3rem;
  border-radius: 2px;
  max-height: 200px;
  overflow: auto;
  margin-top: 0.2rem;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
